import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, CheckCircle, XCircle, Users, Clock, UserCheck, TrendingUp,
  FlaskConical, ClipboardList, FolderDown, MessageSquare, Megaphone, Settings, Target,
  Plus, Pencil, Trash2, Star, Save, X, BarChart3, RotateCcw, Archive, MessageSquareQuote,
  Euro, Wallet, ShoppingBag, Activity, Search, GraduationCap, CalendarDays,
  ArrowUp, ArrowDown, Download, EyeOff, ChevronDown, AlertCircle, Link2, Trophy,
  ScrollText, RefreshCw, ShieldOff, ExternalLink, Mail, MailOpen, MousePointerClick, BellOff, MessageCircle,
  Inbox, Bot,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getH = () => ({ Authorization: `Bearer ${localStorage.getItem('biounica_token')}` });
const PROF_LABELS = { medico:'Medico', veterinario:'Veterinario', naturopata:'Naturopata', operatore_olistico:'Operatore olistico', erborista_parafarmacia:'Erborista / parafarmacia', informatore_farmaceutico:'Inf. Farmaceutico', marketer_influencer:'Marketer / influencer' };
const ROLE_LABELS = { promotore: 'Promotore', user: 'Promotore', editor: 'Editor', admin: 'Amministratore', superadmin: 'Superadmin' };
const ROLE_RANK = { promotore: 0, user: 0, editor: 1, admin: 2, superadmin: 3 };
const rankOf = (r) => ROLE_RANK[r] ?? 0;
const normRole = (r) => (r === 'user' || !r ? 'promotore' : r);
const roleBadgeCls = (r) => r === 'superadmin' ? 'bg-[#12100c]/10 text-[#12100c]'
  : r === 'admin' ? 'bg-[#c6a15b]/15 text-[#8a6620]'
  : r === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-[#4a4238]';

export default function AdminPage() {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin' || user?.is_superadmin;
  const isAdmin = isSuper || user?.role === 'admin' || user?.is_admin;
  const isEditor = user?.role === 'editor' || user?.is_editor;
  // L'editor lavora solo sui contenuti: niente promotori, niente analytics.
  // Collegamento diretto da fuori: l'avviso su WhatsApp porta qui, gia' aperto
  // sulla richiesta giusta. Vale la pena leggerlo una volta sola, all'avvio.
  const arrivo = useRef(new URLSearchParams(window.location.search));
  const [tab, setTab] = useState(() => {
    const t = arrivo.current.get('tab');
    if (t && isAdmin) return t;
    return isAdmin ? 'users' : 'areas';
  });
  const leadDaAprire = isAdmin ? arrivo.current.get('lead') : null;

  const TABS = [
    ...(isAdmin ? [{ id: 'users', label: 'Promotori', icon: Users }] : []),
    ...(isAdmin ? [{ id: 'lead', label: 'Richieste', icon: Inbox }] : []),
    ...(isAdmin ? [{ id: 'analytics', label: 'Analytics', icon: TrendingUp }] : []),
    ...(isAdmin ? [{ id: 'sales', label: 'Vendite', icon: Euro }] : []),
    ...(isAdmin ? [{ id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle }] : []),
    ...(isAdmin ? [{ id: 'casi', label: 'Casi', icon: MessageSquareQuote }] : []),
    { id: 'areas', label: 'Aree', icon: Target },
    { id: 'studies', label: 'Studi', icon: FlaskConical },
    { id: 'protocols', label: 'Protocolli', icon: ClipboardList },
    { id: 'materials', label: 'Materiali', icon: FolderDown },
    { id: 'feedbacks', label: 'Feedback', icon: MessageSquare },
    { id: 'announcements', label: 'Comunicazioni', icon: Megaphone },
    ...(isAdmin ? [{ id: 'trash', label: 'Cestino', icon: Archive }] : []),
    ...(isSuper ? [{ id: 'audit', label: 'Registro', icon: ScrollText }] : []),
    ...(isAdmin ? [{ id: 'settings', label: 'Impostazioni', icon: Settings }] : []),
  ];

  return (
    <div className="max-w-6xl" data-testid="admin-page">
      <div className="mb-5 flex items-center gap-3">
        <h1 className="font-heading text-2xl font-bold text-[#12100c]">Area amministrazione</h1>
        <Badge className={`text-[10px] border-none ${roleBadgeCls(user?.role)}`}>
          {ROLE_LABELS[user?.role] || 'Editor'}
        </Badge>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-3 mb-5 scrollbar-hide" style={{ scrollbarWidth: 'none' }} data-testid="admin-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                tab === t.id ? 'bg-[#12100c] text-white' : 'text-[#4a4238] hover:bg-gray-100'}`}
              data-testid={`admin-tab-${t.id}`}>
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'users' && isAdmin && <UsersSection me={user} />}
      {tab === 'lead' && isAdmin && <RichiesteSection apri={leadDaAprire} />}
      {tab === 'analytics' && isAdmin && <AnalyticsSection />}
      {tab === 'sales' && isAdmin && <SalesSection />}
      {tab === 'whatsapp' && isAdmin && <WhatsappSection />}
      {tab === 'casi' && isAdmin && <CasiSection />}
      {tab === 'areas' && <CrudSection table="application_areas" label="Aree di applicazione" endpoint="admin/areas" fields={areaFields} isAdmin={isAdmin} />}
      {tab === 'studies' && <CrudSection table="studies" label="Studi scientifici" endpoint="admin/studies" fields={studyFields} isAdmin={isAdmin} />}
      {tab === 'protocols' && <CrudSection table="protocols" label="Protocolli" endpoint="admin/protocols" fields={protocolFields} isAdmin={isAdmin} />}
      {tab === 'materials' && <CrudSection table="materials" label="Materiali" endpoint="admin/materials" fields={materialFields} isAdmin={isAdmin} />}
      {tab === 'feedbacks' && <CrudSection table="feedbacks" label="Feedback" endpoint="admin/feedbacks" fields={feedbackFields} isAdmin={isAdmin} />}
      {tab === 'announcements' && <CrudSection table="announcements" label="Comunicazioni" endpoint="admin/announcements" fields={announcementFields} isAdmin={isAdmin} />}
      {tab === 'trash' && isAdmin && <TrashSection canPurge={isSuper} />}
      {tab === 'audit' && isSuper && <AuditSection />}
      {tab === 'settings' && isAdmin && <SettingsSection />}
    </div>
  );
}

/* ============ CASI RACCONTATI (Admin only) ============ */
function CasiSection() {
  const [casi, setCasi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/casi`, { headers: getH() })
      .then(r => setCasi(r.data))
      .catch(() => setCasi([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;

  return (
    <div data-testid="admin-casi">
      <div className="mb-4">
        <h2 className="font-heading text-lg font-semibold text-[#12100c]">Casi raccontati</h2>
        <p className="text-xs text-[#4a4238] mt-1">
          Quello che i professionisti vedono coi loro pazienti, dal più recente.
        </p>
      </div>
      {!casi?.length ? (
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardContent className="p-8 text-center text-sm text-[#4a4238]">
            Nessun caso raccontato finora.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {casi.map(c => (
            <Card key={c.id} className="rounded-2xl border-gray-100 shadow-sm" data-testid={`caso-${c.id}`}>
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-[#12100c]">{c.full_name || c.email}</span>
                  <Badge variant="outline" className="text-[10px] text-[#4a4238]">
                    {PROF_LABELS[c.professional_type] || c.professional_type || 'n/d'}
                  </Badge>
                  <span className="text-[10px] text-[#5b5346]">{fmtDateTime(c.created_at)}</span>
                  {c.area_slug && <span className="text-[10px] text-[#5b5346]">· {c.area_slug.replace(/-/g, ' ')}</span>}
                  {c.ricontattabile && (
                    <Badge className="text-[9px] bg-[#5f7d52]/10 text-[#4a5f43] border-none">
                      Disponibile a essere ricontattato
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-[#4a4238] whitespace-pre-wrap leading-relaxed">{c.caso}</p>
                {c.esito && (
                  <p className="text-sm text-[#4a4238] whitespace-pre-wrap leading-relaxed mt-2 pt-2 border-t border-gray-100">
                    <span className="text-[#12100c] font-medium">Com'è andata: </span>{c.esito}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============ USERS SECTION (Admin only) ============ */
function UsersSection({ me }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [dossierId, setDossierId] = useState(null);
  const [accessUser, setAccessUser] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingMail, setSyncingMail] = useState(false);
  const myRank = rankOf(me?.role);
  const canManage = (u) => String(u.id) === String(me?.id) || rankOf(u.role) < myRank;

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/admin/users`, { headers: getH() }); setUsers(r.data); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const syncAccess = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/sync-access`, {}, { headers: getH() });
      const d = r.data;
      if (!d.ok) toast.error(d.reason || 'Sincronizzazione non riuscita');
      else if (!d.opened.length && !d.closed.length) toast.success(`Già allineati: ${d.checked} promotori`);
      else toast.success(`Aggiornati: ${d.opened.length} riaperti, ${d.closed.length} chiusi`);
      fetch();
    } catch { toast.error('Errore'); }
    setSyncing(false);
  };

  const syncFunnelkit = async () => {
    setSyncingMail(true);
    try {
      const r = await axios.post(`${API}/admin/sync-funnelkit`, {}, { headers: getH() });
      const e = r.data?.email || {};
      const c = r.data?.contatti || {};
      toast.success(`Email aggiornate: ${e.totale ?? 0} in archivio, ${e.nuove ?? 0} nuove · ${c.disiscritti ?? 0} disiscritti`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sincronizzazione email non riuscita');
    }
    setSyncingMail(false);
  };

  const startEdit = (u) => {
    setEditForm({ full_name: u.full_name, professional_type: u.professional_type, affiliate_code: u.affiliate_code, role: normRole(u.role), notes: u.notes || '', new_password: '' });
    setEditingUser(u);
  };

  const saveUser = async () => {
    try {
      await axios.patch(`${API}/admin/users/${editingUser.id}`, editForm, { headers: getH() });
      toast.success('Utente aggiornato');
      setEditingUser(null);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;
  const staff = users.filter(u => normRole(u.role) !== 'promotore');
  const promotori = users.filter(u => normRole(u.role) === 'promotore');
  const chiusi = promotori.filter(u => !u.is_approved);

  // Scheda promotore: accessi, provvigioni, vendite, contenuti visti
  if (dossierId) {
    return (
      <UserDossier
        userId={dossierId}
        onClose={() => setDossierId(null)}
        onEdit={() => {
          const u = users.find(x => String(x.id) === String(dossierId));
          if (u) { startEdit(u); setDossierId(null); }
        }}
      />
    );
  }

  // Edit modal
  if (editingUser) {
    return (
      <div data-testid="user-edit-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-[#12100c]">Modifica utente: {editingUser.full_name}</h2>
          <Button variant="ghost" size="sm" onClick={() => setEditingUser(null)}><X className="w-4 h-4 mr-1" /> Chiudi</Button>
        </div>
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div><Label className="text-sm font-medium text-[#12100c]">Nome completo</Label>
              <Input value={editForm.full_name} onChange={e => setEditForm(p => ({...p, full_name: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-sm font-medium text-[#12100c]">Tipo professionista</Label>
              <Select value={editForm.professional_type} onValueChange={v => setEditForm(p => ({...p, professional_type: v}))}>
                <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PROF_LABELS).map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-sm font-medium text-[#12100c]">Codice affiliato</Label>
              <Input value={editForm.affiliate_code} onChange={e => setEditForm(p => ({...p, affiliate_code: e.target.value}))} className="mt-1 rounded-xl" /></div>
            <div><Label className="text-sm font-medium text-[#12100c]">Ruolo</Label>
              <Select value={editForm.role || 'promotore'} disabled={String(editingUser.id) === String(me?.id)}
                      onValueChange={v => setEditForm(p => ({...p, role: v}))}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['promotore', 'editor', 'admin', 'superadmin']
                    .filter(r => rankOf(r) <= (me?.role === 'superadmin' ? 3 : myRank - 1))
                    .map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
              {String(editingUser.id) === String(me?.id)
                ? <p className="text-[10px] text-[#5b5346] mt-1">Non puoi cambiare il tuo stesso ruolo.</p>
                : <p className="text-[10px] text-[#5b5346] mt-1">Puoi assegnare solo ruoli inferiori al tuo.</p>}
            </div>
            <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2">
              <p className="text-[11px] text-[#4a4238]">
                L'accesso al portale non si imposta da qui: rispecchia lo stato dell'affiliato su biounica.it.
              </p>
            </div>
            <div><Label className="text-sm font-medium text-[#12100c]">Note interne</Label>
              <textarea value={editForm.notes} onChange={e => setEditForm(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm" /></div>
            <div><Label className="text-sm font-medium text-[#12100c]">Nuova password (lascia vuoto per non cambiare)</Label>
              <Input type="password" value={editForm.new_password} onChange={e => setEditForm(p => ({...p, new_password: e.target.value}))} placeholder="Min. 8 caratteri" className="mt-1 rounded-xl" data-testid="user-new-password" /></div>
            <div className="flex gap-2 pt-2">
              <Button onClick={saveUser} className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full"><Save className="w-4 h-4 mr-1" /> Salva</Button>
              <Button variant="outline" onClick={() => setEditingUser(null)} className="rounded-full">Annulla</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatMini icon={Users} value={promotori.length} label="Promotori" color="#12100c" />
        <StatMini icon={UserCheck} value={promotori.length - chiusi.length} label="Accesso aperto" color="#5f7d52" />
        <StatMini icon={ShieldOff} value={chiusi.length} label="Accesso chiuso" color="#B45454" />
        <StatMini icon={Settings} value={staff.length} label="Staff" color="#c6a15b" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <Button size="sm" variant="outline" onClick={syncAccess} disabled={syncing} className="rounded-full text-xs">
          {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
          Allinea con AffiliateWP
        </Button>
        <Button size="sm" variant="outline" onClick={syncFunnelkit} disabled={syncingMail} className="rounded-full text-xs">
          {syncingMail ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
          Aggiorna email FunnelKit
        </Button>
        <p className="text-[11px] text-[#5b5346]">
          Chi entra nel portale lo decide biounica.it. L'allineamento gira comunque da solo ogni mezz'ora, le email ogni ora.
        </p>
      </div>

      {chiusi.length > 0 && (
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><ShieldOff className="w-4 h-4" /> Accesso chiuso ({chiusi.length})</CardTitle></CardHeader>
          <CardContent className="p-0"><div className="divide-y divide-gray-100">{chiusi.map(u => (
            <UserRow key={u.id} user={u} onEdit={canManage(u) ? () => startEdit(u) : null}
                     onView={() => setDossierId(u.id)} onAccess={() => setAccessUser(u)} />
          ))}</div></CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c]">Promotori ({promotori.length})</CardTitle></CardHeader>
        <CardContent className="p-0"><div className="divide-y divide-gray-100">{promotori.map(u => (
          <UserRow key={u.id} user={u} onEdit={canManage(u) ? () => startEdit(u) : null}
                   onView={() => setDossierId(u.id)} onAccess={() => setAccessUser(u)} />
        ))}</div></CardContent>
      </Card>

      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c]">Staff ({staff.length})</CardTitle></CardHeader>
        <CardContent className="p-0"><div className="divide-y divide-gray-100">{staff.map(u => (
          <UserRow key={u.id} user={u} onEdit={canManage(u) ? () => startEdit(u) : null}
                   onView={() => setDossierId(u.id)} />
        ))}</div></CardContent>
      </Card>

      {/* Chiudere un accesso è un'operazione di biounica.it, non del portale */}
      <Dialog open={!!accessUser} onOpenChange={(o) => !o && setAccessUser(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="text-[#12100c] text-base">Accesso di {accessUser?.full_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-[#4a4238]">
            <p>
              L'accesso al portale non si apre e non si chiude da qui: rispecchia sempre lo stato
              dell'affiliato su <strong>biounica.it</strong>.
            </p>
            <p>Per chiudere o riaprire l'accesso a questo promotore:</p>
            <ol className="list-decimal pl-5 space-y-1 text-[13px]">
              <li>entra in <strong>biounica.it</strong>, area <strong>AffiliateWP</strong>;</li>
              <li>agisci sull'affiliato come hai sempre fatto (stato, oppure rimozione);</li>
              <li>torna qui e premi <strong>Allinea con AffiliateWP</strong>, oppure aspetta la sincronizzazione automatica.</li>
            </ol>
            <p className="text-[12px] text-[#5b5346]">
              Il profilo, gli accessi registrati, le note e lo storico vendite restano comunque: si chiude
              solo la porta, non si cancella nulla.
            </p>
            {accessUser && !accessUser.is_approved && (
              <p className="text-[12px] text-[#B45454]">Stato attuale: accesso chiuso. {accessUser.blocked_reason || ''}</p>
            )}
          </div>
          <DialogFooter>
            <a href="https://biounica.it/wp-admin/admin.php?page=affiliate-wp-affiliates" target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-[#12100c] hover:bg-[#5f7d52] rounded-full px-4 py-2">
              Apri AffiliateWP <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserRow({ user, onEdit, onView, onAccess }) {
  const role = normRole(user.role);
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-4 py-3 hover:bg-gray-50/50" data-testid={`user-${user.id}`}>
      <button type="button" onClick={onView} className="flex-1 min-w-0 text-left group" data-testid={`view-user-${user.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-[#12100c] truncate group-hover:underline">{user.full_name}</p>
          {role !== 'promotore' && <Badge className={`text-[8px] border-none ${roleBadgeCls(role)}`}>{ROLE_LABELS[role]}</Badge>}
          {role === 'promotore' && (user.is_approved
            ? <Badge className="text-[8px] bg-[#5f7d52]/10 text-[#4a5f43] border-none">Accesso aperto</Badge>
            : <Badge className="text-[8px] bg-red-100 text-red-700 border-none">Accesso chiuso</Badge>)}
        </div>
        <p className="text-[10px] text-[#4a4238]">{user.email}{user.professional_type ? ` - ${PROF_LABELS[user.professional_type] || user.professional_type}` : ''}{user.affiliate_code ? ` - ${user.affiliate_code}` : ''}</p>
        {user.last_login && <p className="text-[10px] text-[#5b5346]">Ultimo accesso: {fmtDateTime(user.last_login)}</p>}
      </button>
      <div className="flex gap-2 shrink-0">
        {onEdit && (
          <Button size="sm" variant="outline" onClick={onEdit} className="rounded-full h-8 px-3 text-xs border-gray-300 text-[#12100c]" data-testid={`edit-user-${user.id}`}>
            <Pencil className="w-3 h-3 mr-1.5" /> Modifica
          </Button>
        )}
        {onAccess && (
          <Button size="sm" variant="outline" onClick={onAccess} className="rounded-full h-8 px-3 text-xs border-gray-300 text-[#4a4238]" data-testid={`access-user-${user.id}`}>
            <ShieldOff className="w-3 h-3 mr-1.5" /> Accesso
          </Button>
        )}
      </div>
    </div>
  );
}

/* ============ ANALYTICS ============ */
const PAGE_LABELS = {
  '/dashboard': 'Dashboard',
  '/aree-applicazione': 'Aree di applicazione',
  '/trova-indicazione': 'Trova indicazione',
  '/conosci-umiful': 'Conosci UMIFUL',
  '/approfondimenti-scientifici': 'Approfondimenti scientifici',
  '/protocolli': 'Protocolli',
  '/le-mie-provvigioni': 'Le mie provvigioni',
  '/materiali-download': 'Materiali & download',
  '/feedback-professionisti': 'Feedback',
  '/profile': 'Profilo',
  '/admin': 'Amministrazione',
};
const pageLabel = (p) => PAGE_LABELS[p] || p;
const fmtDate = (iso) => { if (!iso) return null; const d = new Date(iso); return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }); };
const fmtDateTime = (iso) => { if (!iso) return null; const d = new Date(iso); return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); };

function AnalyticsSection() {
  const [days, setDays] = useState(30);
  const [access, setAccess] = useState(null);
  const [activity, setActivity] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/access-stats?days=${days}`, { headers: getH() }),
      axios.get(`${API}/admin/user-activity`, { headers: getH() }),
      axios.get(`${API}/admin/search-stats`, { headers: getH() }),
    ]).then(([a, u, s]) => {
      setAccess(a.data); setActivity(u.data || []); setSearches(s.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;

  const byDay = access?.logins_by_day || [];
  const maxDay = Math.max(1, ...byDay.map(d => d.logins));
  const topPages = access?.top_pages || [];
  const maxPage = topPages[0]?.views || 1;
  const entered = activity.filter(u => u.last_login);
  const neverEntered = activity.filter(u => !u.last_login);

  return (
    <div className="space-y-4" data-testid="admin-analytics">
      {/* Selettore periodo */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-[#4a4238]">Periodo:</span>
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${days === d ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200'}`}>
            {d} giorni
          </button>
        ))}
      </div>

      {/* KPI accessi */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatMini icon={TrendingUp} value={access?.logins_total ?? 0} label="Accessi" color="#12100c" />
        <StatMini icon={UserCheck} value={access?.active_users ?? 0} label="Utenti attivi" color="#5f7d52" />
        <StatMini icon={BarChart3} value={access?.pageviews_total ?? 0} label="Pagine viste" color="#c6a15b" />
        <StatMini icon={XCircle} value={neverEntered.length} label="Mai entrati" color="#B45454" />
      </div>

      {/* Accessi per giorno */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Accessi per giorno</CardTitle></CardHeader>
        <CardContent>
          {byDay.length === 0 ? <p className="text-xs text-[#4a4238]">Nessun accesso nel periodo</p> : (
            <div className="flex items-end gap-1 h-32">
              {byDay.map((d, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.day}: ${d.logins} accessi, ${d.users} utenti`}>
                  <div className="w-full bg-[#5f7d52] rounded-t transition-all group-hover:bg-[#12100c]" style={{ height: `${(d.logins / maxDay) * 100}%`, minHeight: '2px' }} />
                  {byDay.length <= 31 && <span className="text-[8px] text-[#5b5346] rotate-0 truncate w-full text-center">{fmtDate(d.day)}</span>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top pagine */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Pagine più visitate</CardTitle></CardHeader>
        <CardContent>{topPages.length === 0 ? <p className="text-xs text-[#4a4238]">Nessuna pagina registrata</p> : (
          <div className="space-y-2.5">{topPages.map((p, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-[#12100c] font-medium w-40 truncate" title={p.path}>{pageLabel(p.path)}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#5f7d52] rounded-full" style={{ width: `${Math.min((p.views / maxPage) * 100, 100)}%` }} /></div>
              <Badge variant="outline" className="text-[10px]">{p.views}</Badge>
              <span className="text-[10px] text-[#5b5346] w-16 text-right">{p.users} utenti</span>
            </div>
          ))}</div>
        )}</CardContent>
      </Card>

      {/* Attività utenti: chi entra e chi no */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Users className="w-4 h-4" /> Attività utenti ({entered.length} entrati · {neverEntered.length} mai entrati)</CardTitle></CardHeader>
        <CardContent className="px-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[#5b5346] text-[10px] uppercase tracking-wide border-b border-gray-100">
                  <th className="text-left font-semibold px-4 py-2">Utente</th>
                  <th className="text-left font-semibold px-2 py-2">Ultimo accesso</th>
                  <th className="text-right font-semibold px-2 py-2">Accessi</th>
                  <th className="text-right font-semibold px-2 py-2">Pagine</th>
                  <th className="text-right font-semibold px-4 py-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {activity.map((u) => (
                  <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="px-4 py-2">
                      <div className="font-medium text-[#12100c] truncate max-w-[180px]">{u.full_name}</div>
                      <div className="text-[10px] text-[#5b5346] truncate max-w-[180px]">{u.email}</div>
                    </td>
                    <td className="px-2 py-2">
                      {u.last_login
                        ? <span className="text-[#4a4238]">{fmtDateTime(u.last_login)}</span>
                        : <Badge variant="outline" className="text-[10px] text-[#B45454] border-[#E8C9C9]">Mai entrato</Badge>}
                    </td>
                    <td className="px-2 py-2 text-right text-[#4a4238]">{u.logins}</td>
                    <td className="px-2 py-2 text-right text-[#4a4238]">{u.pageviews}</td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {u.email_unsubscribed
                        ? <Badge variant="outline" className="text-[10px] text-[#B45454] border-[#E8C9C9]">Disiscritto</Badge>
                        : u.crm_stato === 'non_verificato'
                          ? <Badge variant="outline" className="text-[10px] text-[#8a6620] border-[#E8DCB4]" title="Contatto creato senza un passaggio che registrasse il consenso: le email non gli partono">Non verificato</Badge>
                          : <span className="text-[#4a4238]">{u.emails_sent || 0}<span className="text-[#5b5346]"> · {u.emails_opened || 0} aperte</span></span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Ricerche frequenti (esistente) */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Ricerche frequenti</CardTitle></CardHeader>
        <CardContent>{searches.length === 0 ? <p className="text-xs text-[#4a4238]">Nessuna ricerca registrata</p> : (
          <div className="space-y-2.5">{searches.map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-xs font-bold text-[#4a4238] w-5">{i+1}.</span>
              <span className="text-sm text-[#12100c] font-medium w-32 truncate">{s.search_term}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#5f7d52] rounded-full" style={{ width: `${Math.min((s.count / (searches[0]?.count || 1)) * 100, 100)}%` }} /></div>
              <Badge variant="outline" className="text-[10px]">{s.count}</Badge>
            </div>
          ))}</div>
        )}</CardContent>
      </Card>
    </div>
  );
}

/* ============ REGISTRO AZIONI (solo superadmin) ============ */
const AUDIT_LABELS = {
  cestina_contenuto: 'Contenuto cestinato',
  ripristina_contenuto: 'Contenuto ripristinato',
  elimina_definitivamente: 'Eliminazione definitiva',
  cambio_ruolo: 'Cambio ruolo',
  modifica_profilo: 'Profilo modificato',
  note_profilo: 'Note aggiornate',
  cambio_password_altrui: 'Password cambiata da staff',
  sync_accessi: 'Allineamento accessi',
};
const AUDIT_CLS = {
  elimina_definitivamente: 'bg-red-100 text-red-700',
  cambio_ruolo: 'bg-[#c6a15b]/15 text-[#8a6620]',
  cambio_password_altrui: 'bg-[#c6a15b]/15 text-[#8a6620]',
  ripristina_contenuto: 'bg-[#5f7d52]/10 text-[#4a5f43]',
};

function AuditSection() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/audit`, { params: filter ? { action: filter } : {}, headers: getH() })
      .then(r => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="space-y-3" data-testid="admin-audit">
      <div className="flex gap-1.5 flex-wrap">
        <button onClick={() => setFilter('')}
          className={`px-3 py-1 rounded-full text-xs font-medium border ${!filter ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200'}`}>Tutto</button>
        {Object.keys(AUDIT_LABELS).map(a => (
          <button key={a} onClick={() => setFilter(a)}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${filter === a ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200'}`}>
            {AUDIT_LABELS[a]}
          </button>
        ))}
      </div>
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto" /></div>
            : rows.length === 0 ? <p className="p-6 text-sm text-[#4a4238] text-center">Nessuna azione registrata.</p> : (
            <div className="divide-y divide-gray-50">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center gap-3 px-4 py-2.5">
                  <Badge className={`text-[9px] border-none shrink-0 ${AUDIT_CLS[r.action] || 'bg-gray-100 text-[#4a4238]'}`}>
                    {AUDIT_LABELS[r.action] || r.action}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#12100c] truncate">
                      {r.target_label || r.target_id || 'n/d'}
                      {r.target_type ? <span className="text-[#5b5346]"> · {r.target_type}</span> : null}
                    </p>
                    <p className="text-[10px] text-[#5b5346] truncate">
                      {r.actor_email || 'sistema'}
                      {r.actor_role ? ` (${ROLE_LABELS[r.actor_role] || r.actor_role})` : ''}
                      {r.detail ? ` · ${JSON.stringify(r.detail)}` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] text-[#5b5346] shrink-0">{fmtDateTime(r.at)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ SCHEDA PROMOTORE (Admin only) ============ */
const fmtEur = (n) => (Number(n) || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDay = (iso) => { if (!iso) return null; const d = new Date(iso); return isNaN(d.getTime()) ? iso : d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); };
const fmtMonth = (key) => { const [y, m] = String(key).split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('it-IT', { month: 'short', year: '2-digit' }); };
const REF_STATUS = {
  paid: { label: 'Pagata', cls: 'bg-[#5f7d52]/10 text-[#4a5f43]' },
  unpaid: { label: 'Da liquidare', cls: 'bg-[#c6a15b]/15 text-[#8a6620]' },
  pending: { label: 'In attesa', cls: 'bg-gray-100 text-[#4a4238]' },
  rejected: { label: 'Rifiutata', cls: 'bg-red-100 text-red-700' },
};
const refStatus = (s) => REF_STATUS[s] || { label: s || 'n/d', cls: 'bg-gray-100 text-[#4a4238]' };

function UserDossier({ userId, onClose, onEdit, backLabel = 'Chiudi' }) {
  const [detail, setDetail] = useState(null);
  const [comm, setComm] = useState(null);
  const [mail, setMail] = useState(null);
  const [failed, setFailed] = useState(false);
  const [allPages, setAllPages] = useState(false);
  const [allSales, setAllSales] = useState(false);
  const [allMail, setAllMail] = useState(false);
  const [consensoAperto, setConsensoAperto] = useState(false);
  const [consensoTel, setConsensoTel] = useState('');
  const [consensoInCorso, setConsensoInCorso] = useState(false);

  const ricaricaComunicazioni = useCallback(() => {
    axios.get(`${API}/admin/users/${userId}/communications`, { headers: getH() })
      .then(r => setMail(r.data))
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    let alive = true;
    setDetail(null); setComm(null); setMail(null); setFailed(false);
    axios.get(`${API}/admin/users/${userId}/activity`, { headers: getH() })
      .then(r => { if (alive) setDetail(r.data); })
      .catch(() => { if (alive) setFailed(true); });
    axios.get(`${API}/admin/users/${userId}/commissions`, { headers: getH() })
      .then(r => { if (alive) setComm(r.data); })
      .catch(() => { if (alive) setComm({ linked: false, available: false, reason: 'error' }); });
    axios.get(`${API}/admin/users/${userId}/communications`, { headers: getH() })
      .then(r => { if (alive) setMail(r.data); })
      .catch(() => { if (alive) setMail({ unavailable: true }); });
    return () => { alive = false; };
  }, [userId]);

  if (failed) return (
    <div data-testid="user-dossier">
      <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4 mr-1" /> {backLabel}</Button>
      <p className="text-sm text-[#4a4238] mt-4">Impossibile caricare la scheda di questo promotore.</p>
    </div>
  );
  if (!detail) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;

  const p = detail.profile || {};
  const commOk = comm && comm.available;
  const sales = (comm?.recent_referrals) || [];
  const pages = detail.top_pages || [];
  const shownPages = allPages ? pages : pages.slice(0, 8);
  const maxViews = pages[0]?.views || 1;
  const shownSales = allSales ? sales : sales.slice(0, 8);
  const byDay = detail.activity_by_day || [];
  const maxDayEvents = Math.max(1, ...byDay.map(d => d.logins + d.pageviews));
  const mailTot = mail?.totals || {};
  const messages = mail?.messages || [];
  const shownMail = allMail ? messages : messages.slice(0, 10);
  const unsubscribed = mail?.crm?.unsubscribed === true;
  const nonVerificato = mail?.crm?.stato === 'non_verificato';
  const wa = mail?.whatsapp || null;
  const waMsgs = wa?.messaggi || [];

  return (
    <div className="space-y-4" data-testid="user-dossier">
      {/* Intestazione */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-heading text-lg font-semibold text-[#12100c]">{p.full_name || detail.full_name}</h2>
            {p.role && p.role !== 'user' && <Badge className={`text-[8px] border-none ${p.role === 'admin' ? 'bg-[#c6a15b]/15 text-[#8a6620]' : 'bg-blue-100 text-blue-700'}`}>{ROLE_LABELS[p.role]}</Badge>}
            <Badge className={`text-[8px] border-none ${p.is_approved ? 'bg-[#5f7d52]/10 text-[#4a5f43]' : 'bg-amber-100 text-amber-700'}`}>{p.is_approved ? 'Approvato' : 'In attesa'}</Badge>
            {comm?.linked && <Badge className="text-[8px] border-none bg-[#12100c]/10 text-[#12100c]">Affiliato #{comm.affiliate_id}</Badge>}
          </div>
          <p className="text-xs text-[#4a4238] mt-1">
            {p.email}
            {p.professional_type && ` · ${PROF_LABELS[p.professional_type] || p.professional_type}`}
            {p.affiliate_code && ` · codice ${p.affiliate_code}`}
            {p.created_at && ` · nel portale dal ${fmtDay(p.created_at)}`}
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          {onEdit && <Button variant="outline" size="sm" onClick={onEdit} className="rounded-full"><Pencil className="w-3.5 h-3.5 mr-1" /> Modifica</Button>}
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4 mr-1" /> {backLabel}</Button>
        </div>
      </div>

      {/* KPI principali: soldi a sinistra, uso del portale a destra */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatMini icon={Wallet} value={commOk ? fmtEur(comm.total_revenue) : 'n/d'} label="Fatturato generato" color="#12100c" />
        <StatMini icon={Euro} value={commOk ? fmtEur(comm.total_amount) : 'n/d'} label="Provvigioni maturate" color="#5f7d52" />
        <StatMini icon={ShoppingBag} value={commOk ? comm.referrals_count : 'n/d'} label="Vendite" color="#c6a15b" />
        <StatMini icon={TrendingUp} value={detail.logins} label="Accessi" color="#12100c" />
        <StatMini icon={BarChart3} value={detail.pageviews} label="Pagine viste" color="#c6a15b" />
        <StatMini icon={Clock} value={fmtDateTime(detail.last_login) || 'Mai'} label="Ultimo accesso" color="#5f7d52" />
      </div>

      {/* Segnali immediati */}
      <div className="flex flex-wrap gap-2">
        {detail.logins === 0 && (
          <div className="flex items-center gap-2 text-xs text-[#B45454] bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5" /> Non ha mai effettuato l'accesso al portale.
          </div>
        )}
        {commOk && comm.referrals_count === 0 && (
          <div className="flex items-center gap-2 text-xs text-[#8a6620] bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5" /> Nessuna vendita registrata.
          </div>
        )}
        {detail.days_since_last_access != null && detail.days_since_last_access >= 30 && (
          <div className="flex items-center gap-2 text-xs text-[#4a4238] bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
            <Clock className="w-3.5 h-3.5" /> Non entra nel portale da {detail.days_since_last_access} giorni.
          </div>
        )}
        {unsubscribed && (
          <div className="flex items-center gap-2 text-xs text-[#B45454] bg-red-50 border border-red-100 rounded-xl px-3 py-2">
            <BellOff className="w-3.5 h-3.5" />
            Si è disiscritto dalle email{mail.crm.unsubscribed_at ? ` il ${fmtDate(mail.crm.unsubscribed_at)}` : ''}: non va scritto.
          </div>
        )}
        {!unsubscribed && nonVerificato && (
          <div className="flex items-center gap-2 text-xs text-[#8a6620] bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            <Mail className="w-3.5 h-3.5" />
            Contatto non verificato nel CRM: non si è mai disiscritto, ma le email non gli partono.
            Si risolve segnandolo come iscritto su FunnelKit.
          </div>
        )}
        {mail && !mail.unavailable && mailTot.sent > 0 && mailTot.opened === 0 && (
          <div className="flex items-center gap-2 text-xs text-[#8a6620] bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            <Mail className="w-3.5 h-3.5" /> Ha ricevuto {mailTot.sent} email e non ne ha mai aperta una.
          </div>
        )}
        {comm && !comm.linked && (
          <div className="flex items-center gap-2 text-xs text-[#4a4238] bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
            <Link2 className="w-3.5 h-3.5" /> Account non collegato ad AffiliateWP: nessun dato di vendita.
          </div>
        )}
        {comm && comm.linked && !comm.available && (
          <div className="flex items-center gap-2 text-xs text-[#4a4238] bg-gray-50 border border-gray-100 rounded-xl px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5" /> Dati provvigioni momentaneamente non disponibili da biounica.it.
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Provvigioni */}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Euro className="w-4 h-4" /> Provvigioni</CardTitle></CardHeader>
          <CardContent>
            {!comm ? <Loader2 className="w-4 h-4 animate-spin text-[#12100c]" /> : !commOk ? (
              <p className="text-xs text-[#4a4238]">Non disponibili.</p>
            ) : (
              <div className="space-y-2 text-xs">
                <DossierRow label="Fatturato generato" value={fmtEur(comm.total_revenue)} strong />
                <DossierRow label="Scontrino medio" value={fmtEur(comm.avg_order)} />
                <DossierRow label="Totale maturato" value={fmtEur(comm.total_amount)} strong />
                <DossierRow label="Già liquidate" value={fmtEur(comm.earnings)} />
                <DossierRow label="Da liquidare" value={fmtEur(comm.unpaid_earnings)} />
                <DossierRow label="Incidenza sul fatturato" value={comm.total_revenue ? `${(comm.total_amount / comm.total_revenue * 100).toFixed(1)}%` : 'n/d'} />
                <DossierRow label="Provvigione media" value={fmtEur(comm.avg_amount)} />
                <DossierRow label="Vendite totali" value={comm.referrals_count} />
                {comm.rejected_count > 0 && <DossierRow label="Vendite rifiutate" value={comm.rejected_count} />}
                <DossierRow label="Tasso" value={comm.rate ? `${comm.rate}${comm.rate_type === 'percentage' ? '%' : ' €'}` : 'n/d'} />
                <DossierRow label="Prima vendita" value={fmtDay(comm.first_sale) || 'n/d'} />
                <DossierRow label="Ultima vendita" value={fmtDay(comm.last_sale) || 'n/d'} />
                <DossierRow label="Affiliato dal" value={fmtDay(comm.date_registered) || 'n/d'} />
                <DossierRow label="Visite tracciate al link" value={comm.visits ?? 0} />
                {comm.by_month?.length > 0 && (
                  <div className="pt-2">
                    <p className="text-[10px] uppercase tracking-wide text-[#5b5346] mb-1.5">Fatturato per mese</p>
                    <div className="flex items-end gap-1 h-16">
                      {comm.by_month.map((m, i) => {
                        const max = Math.max(...comm.by_month.map(x => x.revenue), 1);
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${m.month}: ${m.referrals} vendite, ${fmtEur(m.revenue)} di fatturato, ${fmtEur(m.amount)} di provvigioni`}>
                            <div className="w-full bg-[#5f7d52] rounded-t" style={{ height: `${(m.revenue / max) * 100}%`, minHeight: '2px' }} />
                            {comm.by_month.length <= 18 && <span className="text-[7px] text-[#5b5346] truncate w-full text-center">{fmtMonth(m.month)}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Ultime vendite */}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Ultime vendite</CardTitle>
            <p className="text-[10px] text-[#5b5346]">Importo dell'ordine e, a destra, la provvigione.</p>
          </CardHeader>
          <CardContent className="px-0">
            {!comm ? <Loader2 className="w-4 h-4 animate-spin text-[#12100c] mx-4" /> : sales.length === 0 ? (
              <p className="text-xs text-[#4a4238] px-6">Nessuna vendita registrata.</p>
            ) : (
              <>
                <div className="divide-y divide-gray-50">
                  {shownSales.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 px-6 py-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#12100c] truncate" title={s.description}>{s.description || `Ordine ${s.reference || 'n/d'}`}</p>
                        <p className="text-[10px] text-[#5b5346]">{fmtDay(s.date)}{s.reference ? ` · ordine ${s.reference}` : ''}</p>
                      </div>
                      <Badge className={`text-[8px] border-none ${refStatus(s.status).cls}`}>{refStatus(s.status).label}</Badge>
                      <span className="text-xs font-semibold text-[#12100c] w-20 text-right" title={s.order_estimated ? 'Stimato sul valore dei prodotti' : 'Totale ordine'}>
                        {fmtEur(s.order_total)}{s.order_estimated ? '*' : ''}
                      </span>
                      <span className="text-[10px] text-[#5b5346] w-14 text-right" title="Provvigione">{fmtEur(s.amount)}</span>
                    </div>
                  ))}
                </div>
                {sales.length > 8 && (
                  <button onClick={() => setAllSales(v => !v)} className="text-[10px] text-[#4a5f43] hover:underline px-6 pt-2">
                    {allSales ? 'Mostra meno' : `Mostra tutte (${sales.length})`}
                  </button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Attività nel portale */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Activity className="w-4 h-4" /> Attività nel portale</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <DossierRow label="Ultimo accesso" value={fmtDateTime(detail.last_login) || 'Mai'} stacked />
            <DossierRow label="Ultima attività" value={fmtDateTime(detail.last_activity) || 'n/d'} stacked />
            <DossierRow label="Primo accesso" value={fmtDateTime(detail.first_login) || 'n/d'} stacked />
            <DossierRow label="Giorni dall'ultima visita" value={detail.days_since_last_access != null ? detail.days_since_last_access : 'n/d'} stacked />
          </div>
          {byDay.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#5b5346] mb-1.5">Ultimi 90 giorni</p>
              <div className="flex items-end gap-[2px] h-12">
                {byDay.map((d, i) => (
                  <div key={i} className="flex-1 bg-[#5f7d52] rounded-t" title={`${d.day}: ${d.logins} accessi, ${d.pageviews} pagine`}
                    style={{ height: `${((d.logins + d.pageviews) / maxDayEvents) * 100}%`, minHeight: '2px' }} />
                ))}
              </div>
            </div>
          )}
          {detail.recent_logins?.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#5b5346] mb-1.5">Accessi recenti</p>
              <div className="flex flex-wrap gap-2">
                {detail.recent_logins.map((t, i) => <Badge key={i} variant="outline" className="text-[10px] text-[#4a4238]">{fmtDateTime(t)}</Badge>)}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Pagine viste */}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Pagine del portale viste</CardTitle></CardHeader>
          <CardContent>
            {pages.length === 0 ? <p className="text-xs text-[#4a4238]">Nessuna pagina registrata.</p> : (
              <div className="space-y-2.5">
                {shownPages.map((pg, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-[#12100c] font-medium w-36 truncate" title={pg.path}>{pageLabel(pg.path)}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#5f7d52] rounded-full" style={{ width: `${Math.min((pg.views / maxViews) * 100, 100)}%` }} /></div>
                    <Badge variant="outline" className="text-[10px]">{pg.views}</Badge>
                    <span className="text-[10px] text-[#5b5346] w-20 text-right">{fmtDate(pg.last_seen)}</span>
                  </div>
                ))}
                {pages.length > 8 && (
                  <button onClick={() => setAllPages(v => !v)} className="text-[10px] text-[#4a5f43] hover:underline">
                    {allPages ? 'Mostra meno' : `Mostra tutte (${pages.length})`}
                  </button>
                )}
              </div>
            )}
            {detail.never_seen_pages?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-[10px] uppercase tracking-wide text-[#5b5346] mb-1.5 flex items-center gap-1"><EyeOff className="w-3 h-3" /> Mai aperte</p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.never_seen_pages.map((path, i) => <Badge key={i} variant="outline" className="text-[10px] text-[#5b5346]">{pageLabel(path)}</Badge>)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cronologia */}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Clock className="w-4 h-4" /> Cronologia recente</CardTitle></CardHeader>
          <CardContent>
            {!detail.timeline?.length ? <p className="text-xs text-[#4a4238]">Nessun evento registrato.</p> : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {detail.timeline.map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${e.event_type === 'login' ? 'bg-[#c6a15b]' : 'bg-[#5f7d52]'}`} />
                    <span className="text-[#12100c] flex-1 truncate">{e.event_type === 'login' ? 'Accesso al portale' : pageLabel(e.path)}</span>
                    <span className="text-[10px] text-[#5b5346] shrink-0">{fmtDateTime(e.at)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Comunicazioni ricevute */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Mail className="w-4 h-4" /> Comunicazioni ricevute</CardTitle>
          <p className="text-[10px] text-[#5b5346]">
            Email spedite da FunnelKit su biounica.it, con aperture e click.
            {mail?.sync?.last_sync && ` Aggiornate il ${fmtDateTime(mail.sync.last_sync)}.`}
          </p>
        </CardHeader>
        <CardContent className="px-0">
          {!mail ? <Loader2 className="w-4 h-4 animate-spin text-[#12100c] mx-6" /> : mail.unavailable ? (
            <p className="text-xs text-[#4a4238] px-6">Dati delle comunicazioni non disponibili.</p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-6">
                <StatMini icon={Mail} value={mailTot.sent ?? 0} label="Email ricevute" color="#12100c" />
                <StatMini icon={MailOpen} value={mailTot.open_rate != null ? `${mailTot.open_rate}%` : 'n/d'} label={`Aperte (${mailTot.opened ?? 0})`} color="#5f7d52" />
                <StatMini icon={MousePointerClick} value={mailTot.click_rate != null ? `${mailTot.click_rate}%` : 'n/d'} label={`Con click (${mailTot.clicked ?? 0})`} color="#c6a15b" />
                <StatMini icon={Clock} value={fmtDate(mailTot.last_sent) || 'Mai'} label="Ultima email" color="#12100c" />
              </div>

              <div className="px-6 flex flex-wrap items-center gap-2 text-[10px]">
                {mail.crm ? (
                  <>
                    <Badge className={`text-[9px] border-none ${
                      mail.crm.subscribed ? 'bg-[#5f7d52]/10 text-[#4a5f43]'
                        : mail.crm.unsubscribed ? 'bg-red-100 text-red-700'
                        : 'bg-[#c6a15b]/15 text-[#8a6620]'}`}>
                      {mail.crm.subscribed ? 'Iscritto alle email'
                        : mail.crm.unsubscribed ? 'Disiscritto' : 'Non verificato'}
                    </Badge>
                    {mail.crm.phone && <Badge variant="outline" className="text-[9px] text-[#4a4238]">Telefono nel CRM: {mail.crm.phone}</Badge>}
                    {(mail.crm.tags || []).map((t, i) => <Badge key={i} variant="outline" className="text-[9px] text-[#5b5346]">{t}</Badge>)}
                  </>
                ) : (
                  <span className="text-[#5b5346]">Nessun contatto corrispondente nel CRM di biounica.it.</span>
                )}
              </div>

              {messages.length === 0 ? (
                <p className="text-xs text-[#4a4238] px-6 pb-2">Non ha mai ricevuto email da FunnelKit.</p>
              ) : (
                <>
                  <div className="divide-y divide-gray-50">
                    {shownMail.map((m) => (
                      <div key={m.id} className="flex items-center gap-3 px-6 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#12100c] truncate" title={m.subject || m.campaign}>{m.subject || m.campaign || 'Email senza oggetto'}</p>
                          <p className="text-[10px] text-[#5b5346] truncate">{m.kind === 'broadcast' ? 'Invio singolo' : 'Automazione'}{m.campaign ? ` · ${m.campaign}` : ''}</p>
                        </div>
                        {m.opens > 0
                          ? <Badge className="text-[8px] border-none bg-[#5f7d52]/10 text-[#4a5f43]" title={`Prima apertura: ${fmtDateTime(m.first_open)}`}>Aperta{m.opens > 1 ? ` ×${m.opens}` : ''}</Badge>
                          : <Badge variant="outline" className="text-[8px] text-[#5b5346]">Non aperta</Badge>}
                        {m.clicks > 0 && <Badge className="text-[8px] border-none bg-[#c6a15b]/15 text-[#8a6620]" title={`Primo click: ${fmtDateTime(m.first_click)}`}>Click{m.clicks > 1 ? ` ×${m.clicks}` : ''}</Badge>}
                        <span className="text-[10px] text-[#5b5346] w-24 text-right shrink-0">{fmtDateTime(m.sent_at)}</span>
                      </div>
                    ))}
                  </div>
                  {messages.length > 10 && (
                    <button onClick={() => setAllMail(v => !v)} className="text-[10px] text-[#4a5f43] hover:underline px-6 pt-1">
                      {allMail ? 'Mostra meno' : `Mostra tutte (${messages.length})`}
                    </button>
                  )}
                </>
              )}

              {/* WhatsApp */}
              <div className="border-t border-gray-100 pt-3">
                <div className="px-6 flex flex-wrap items-center gap-2">
                  <p className="text-[10px] uppercase tracking-wide text-[#5b5346] flex items-center gap-1 mr-1">
                    <MessageCircle className="w-3 h-3" /> WhatsApp
                  </p>
                  {!wa?.attivo ? (
                    <span className="text-[10px] text-[#5b5346]">Canale non ancora configurato.</span>
                  ) : !wa.conversazione ? (
                    <span className="text-[10px] text-[#5b5346]">Nessun numero registrato, nessun messaggio scambiato.</span>
                  ) : (
                    <>
                      <Badge className={`text-[9px] border-none ${wa.conversazione.stop ? 'bg-red-100 text-red-700' : 'bg-[#5f7d52]/10 text-[#4a5f43]'}`}>
                        {wa.conversazione.stop ? 'Ha chiesto STOP' : 'Raggiungibile'}
                      </Badge>
                      <Badge variant="outline" className="text-[9px] text-[#4a4238]">{wa.conversazione.telefono}</Badge>
                      {wa.conversazione.opt_in_at && <span className="text-[10px] text-[#5b5346]">dal {fmtDate(wa.conversazione.opt_in_at)}{wa.conversazione.opt_in_fonte ? ` · ${wa.conversazione.opt_in_fonte}` : ''}</span>}
                      {wa.finestra_aperta && <Badge className="text-[9px] border-none bg-[#c6a15b]/15 text-[#8a6620]">Finestra 24h aperta</Badge>}
                      <span className="text-[10px] text-[#5b5346]">{wa.inviati} inviati · {wa.ricevuti} ricevuti</span>
                    </>
                  )}
                  {wa?.attivo && !consensoAperto && (
                    <button
                      onClick={() => { setConsensoTel(wa.conversazione?.telefono || wa.telefono_suggerito || ''); setConsensoAperto(true); }}
                      className="text-[10px] text-[#4a5f43] hover:underline"
                      data-testid="wa-optin-apri"
                    >
                      {wa.conversazione?.stop
                        ? 'Riattiva: lo ha chiesto lui'
                        : wa.conversazione ? 'Correggi il numero' : 'Registra il numero'}
                    </button>
                  )}
                </div>
                {/* Il numero arriva dalla telefonata di attivazione: qui si registra
                    con data e ora. Su un numero che ha detto STOP questo vale come
                    riattivazione, quindi si fa solo se lo ha chiesto la persona. */}
                {consensoAperto && (
                  <div className="px-6 pt-2 flex flex-wrap items-center gap-2" data-testid="wa-optin-form">
                    <Input
                      value={consensoTel}
                      onChange={(e) => setConsensoTel(e.target.value)}
                      placeholder="+39 333 1234567"
                      className="h-8 w-48 text-xs rounded-lg"
                    />
                    <Button
                      size="sm"
                      disabled={consensoInCorso || !consensoTel.trim()}
                      onClick={async () => {
                        setConsensoInCorso(true);
                        try {
                          await axios.post(`${API}/admin/whatsapp/opt-in`,
                            { telefono: consensoTel.trim(), fonte: 'telefonata', user_id: userId },
                            { headers: getH() });
                          toast.success('Numero registrato con data e ora.');
                          setConsensoAperto(false);
                          ricaricaComunicazioni();
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Non è stato possibile registrare il numero');
                        } finally { setConsensoInCorso(false); }
                      }}
                      className="h-8 text-xs bg-[#12100c] hover:bg-[#5f7d52] rounded-lg"
                    >
                      Registra
                    </Button>
                    <button onClick={() => setConsensoAperto(false)} className="text-[10px] text-[#5b5346] hover:underline">Annulla</button>
                    <span className="text-[10px] text-[#5b5346]">Fonte: telefonata di attivazione.</span>
                  </div>
                )}
                {waMsgs.length > 0 && (
                  <div className="divide-y divide-gray-50 mt-2">
                    {waMsgs.slice(0, 10).map((m) => (
                      <div key={m.id} className="flex items-center gap-3 px-6 py-2">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${m.direzione === 'in' ? 'bg-[#c6a15b]' : 'bg-[#5f7d52]'}`}
                              title={m.direzione === 'in' ? 'Ricevuto' : 'Inviato'} />
                        <p className="text-xs text-[#12100c] flex-1 truncate" title={m.testo}>{m.testo}</p>
                        {m.stato && <Badge variant="outline" className="text-[8px] text-[#5b5346]">{m.stato}</Badge>}
                        <span className="text-[10px] text-[#5b5346] w-24 text-right shrink-0">{fmtDateTime(m.at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {detail.casi?.length > 0 && (
        <Card className="rounded-2xl border-gray-100 shadow-sm mb-4" data-testid="dossier-casi">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-[#12100c] flex items-center gap-2">
              <MessageSquareQuote className="w-4 h-4" /> Casi raccontati ({detail.casi.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {detail.casi.map((c, i) => (
              <div key={i} className="rounded-xl border border-gray-100 p-3">
                <div className="flex items-center gap-2 text-[10px] text-[#5b5346] mb-1">
                  <span>{fmtDateTime(c.created_at)}</span>
                  {c.area_slug && <span>· {c.area_slug.replace(/-/g, ' ')}</span>}
                  {c.ricontattabile && <Badge className="text-[9px] bg-[#5f7d52]/10 text-[#4a5f43] border-none">Disponibile a essere ricontattato</Badge>}
                </div>
                <p className="text-xs text-[#4a4238] whitespace-pre-wrap leading-relaxed">{c.caso}</p>
                {c.esito && (
                  <p className="text-xs text-[#4a4238] whitespace-pre-wrap leading-relaxed mt-2 pt-2 border-t border-gray-100">
                    <span className="text-[#12100c] font-medium">Com'è andata: </span>{c.esito}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Ricerche */}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Search className="w-4 h-4" /> Ricerche fatte ({detail.searches_total})</CardTitle></CardHeader>
          <CardContent>
            {!detail.searches?.length ? <p className="text-xs text-[#4a4238]">Nessuna ricerca registrata.</p> : (
              <div className="space-y-1.5">
                {detail.searches.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-[#12100c] flex-1 truncate">{s.search_term}</span>
                    <Badge variant="outline" className="text-[10px] text-[#5b5346]">{s.results_found} risultati</Badge>
                    <span className="text-[10px] text-[#5b5346]">{fmtDate(s.at)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attività e note */}
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><GraduationCap className="w-4 h-4" /> Attività e note</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-xs">
            <DossierRow label="Casi raccontati" value={detail.casi?.length ?? 0} />
            <DossierRow label="Sessioni del wizard" value={detail.wizard?.sessions ?? 0} />
            {detail.wizard?.last && <DossierRow label="Ultimo wizard" value={fmtDateTime(detail.wizard.last)} />}
            <div className="pt-2 border-t border-gray-100">
              <p className="text-[10px] uppercase tracking-wide text-[#5b5346] mb-1">Note interne</p>
              <p className="text-xs text-[#4a4238] whitespace-pre-wrap">{p.notes || 'Nessuna nota.'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DossierRow({ label, value, strong, stacked }) {
  if (stacked) return (
    <div><p className="text-[10px] uppercase tracking-wide text-[#5b5346]">{label}</p><p className="text-xs font-medium text-[#12100c]">{value}</p></div>
  );
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[#4a4238]">{label}</span>
      <span className={strong ? 'font-semibold text-[#12100c]' : 'text-[#12100c]'}>{value}</span>
    </div>
  );
}

/* ============ WHATSAPP (Admin only) ============ */
function WhatsappSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [aperta, setAperta] = useState(null);

  const carica = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/whatsapp`, { headers: getH() })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { carica(); }, [carica]);

  const syncTemplate = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/whatsapp/templates/sync`, {}, { headers: getH() });
      toast.success(`Template riallineati: ${r.data.template} (${r.data.approvati} approvati)`);
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Meta non ha risposto');
    }
    setSyncing(false);
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;
  if (!data) return <p className="text-sm text-[#4a4238]">Impossibile caricare lo stato di WhatsApp.</p>;

  const cfg = data.configurazione || {};
  const tot = data.totali || {};
  const ragg = data.raggiungibili || {};

  return (
    <div className="space-y-4" data-testid="admin-whatsapp">
      {cfg.mancanti?.length > 0 && (
        <Card className="rounded-2xl border-amber-100 shadow-sm bg-amber-50/60">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-[#12100c] flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#8a6620]" /> Canale non ancora operativo
            </p>
            <p className="text-xs text-[#4a4238] mt-1.5">
              Mancano nel <code>.env</code> del backend: {cfg.mancanti.join(', ')}.
              Finché non ci sono, il portale non spedisce e non riceve nulla.
            </p>
            <p className="text-[11px] text-[#5b5346] mt-2">
              Indirizzo del webhook da dare a Meta: <code>https://pro.biounica.it/api/whatsapp/webhook</code>
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatMini icon={MessageCircle} value={tot.inviati || 0} label="Messaggi inviati" color="#12100c" />
        <StatMini icon={MessageSquare} value={tot.ricevuti || 0} label="Risposte ricevute" color="#5f7d52" />
        <StatMini icon={UserCheck} value={ragg.raggiungibili || 0} label={`Raggiungibili (su ${ragg.con_numero || 0} con numero)`} color="#c6a15b" />
        <StatMini icon={AlertCircle} value={tot.errori || 0} label="Invii in errore" color="#B45454" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Template</CardTitle>
            <Button size="sm" variant="outline" onClick={syncTemplate} disabled={syncing} className="rounded-full text-xs">
              {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Rileggi da Meta
            </Button>
          </CardHeader>
          <CardContent>
            {!data.template?.length ? (
              <p className="text-xs text-[#4a4238]">Nessun template. Si creano su WhatsApp Manager e poi si rileggono da qui.</p>
            ) : (
              <div className="space-y-2">
                {data.template.map((t, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Badge className={`text-[8px] border-none shrink-0 mt-0.5 ${t.status === 'APPROVED' ? 'bg-[#5f7d52]/10 text-[#4a5f43]' : t.status === 'REJECTED' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-[#4a4238]'}`}>
                      {t.status === 'APPROVED' ? 'Approvato' : t.status === 'REJECTED' ? 'Rifiutato' : 'In attesa'}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-[#12100c] truncate">{t.name} <span className="text-[#5b5346] font-normal">· {t.language} · {t.category}</span></p>
                      {t.body && <p className="text-[10px] text-[#5b5346] line-clamp-2">{t.body}</p>}
                    </div>
                    {t.variabili > 0 && <Badge variant="outline" className="text-[9px] text-[#5b5346] shrink-0">{t.variabili} variabili</Badge>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Users className="w-4 h-4" /> Conversazioni ({data.conversazioni?.length || 0})</CardTitle></CardHeader>
          <CardContent className="px-0">
            {!data.conversazioni?.length ? (
              <p className="text-xs text-[#4a4238] px-6">Nessuna conversazione. Compariranno qui appena qualcuno dà il consenso o risponde.</p>
            ) : (
              <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
                {data.conversazioni.map((c) => (
                  <button key={c.telefono} onClick={() => setAperta(c.telefono === aperta ? null : c.telefono)}
                          className={`w-full text-left flex items-center gap-3 px-6 py-2 hover:bg-gray-50/60 ${c.telefono === aperta ? 'bg-[#5f7d52]/5' : ''}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-[#12100c] truncate">{c.full_name || c.nome_contatto || c.telefono}</p>
                      <p className="text-[10px] text-[#5b5346] truncate">{c.telefono}{c.email ? ` · ${c.email}` : ''}</p>
                    </div>
                    {c.finestra_aperta && <Badge className="text-[8px] border-none bg-[#c6a15b]/15 text-[#8a6620]">24h aperta</Badge>}
                    <Badge className={`text-[8px] border-none ${c.stop ? 'bg-red-100 text-red-700' : 'bg-[#5f7d52]/10 text-[#4a5f43]'}`}>
                      {c.stop ? 'Uscito' : 'Raggiungibile'}
                    </Badge>
                    <span className="text-[10px] text-[#5b5346] w-10 text-right">{c.messaggi}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {aperta && <WhatsappConversazione telefono={aperta} onClose={() => setAperta(null)} onSent={carica} />}
    </div>
  );
}

/* Campo di risposta WhatsApp: va a capo da solo e si allunga col testo, così
   chi scrive può rileggere tutto prima di inviare. Invio manda il messaggio,
   Maiusc+Invio va a capo. Oltre le 8 righe circa compare la barra di scorrimento. */
function CampoRisposta({ value, onChange, onInvia, placeholder }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <Textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="min-h-9 max-h-48 resize-none overflow-y-auto rounded-xl text-xs py-2 leading-relaxed"
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          onInvia();
        }
      }}
    />
  );
}

function WhatsappConversazione({ telefono, onClose, onSent }) {
  const [data, setData] = useState(null);
  const [testo, setTesto] = useState('');
  const [inviando, setInviando] = useState(false);
  // Il riquadro si apre in fondo alla pagina: senza questi due, chi clicca una
  // conversazione non vede succedere niente e crede che il click non funzioni.
  const riquadro = useRef(null);
  const fondo = useRef(null);

  const carica = useCallback(() => {
    axios.get(`${API}/admin/whatsapp/${telefono}`, { headers: getH() })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [telefono]);
  useEffect(() => { carica(); }, [carica]);

  // Porta il riquadro sotto gli occhi appena si apre o si cambia conversazione.
  useEffect(() => {
    riquadro.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [telefono]);

  // Dentro il riquadro, mostra il fondo: i messaggi sono in ordine di tempo e
  // quello che interessa è l'ultimo.
  useEffect(() => {
    if (data) fondo.current?.scrollIntoView({ block: 'end' });
  }, [data]);

  const invia = async () => {
    setInviando(true);
    try {
      await axios.post(`${API}/admin/whatsapp/send`, { telefono, testo }, { headers: getH() });
      setTesto(''); carica(); onSent?.();
      toast.success('Messaggio inviato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invio non riuscito');
    }
    setInviando(false);
  };

  return (
    <Card ref={riquadro} className="rounded-2xl border-[#5f7d52]/30 shadow-sm scroll-mt-4">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><MessageCircle className="w-4 h-4" /> {telefono}</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent>
        {!data ? <Loader2 className="w-4 h-4 animate-spin text-[#12100c]" /> : (
          <>
            <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
              {!data.messaggi?.length && <p className="text-xs text-[#4a4238]">Nessun messaggio.</p>}
              {data.messaggi?.map((m) => (
                <div key={m.id} className={`flex ${m.direzione === 'in' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${m.direzione === 'in' ? 'bg-gray-100 text-[#12100c]' : 'bg-[#5f7d52]/10 text-[#12100c]'}`}>
                    <p className="text-xs whitespace-pre-wrap">{m.testo}</p>
                    <p className="text-[9px] text-[#5b5346] mt-1">{fmtDateTime(m.at)}{m.stato ? ` · ${m.stato}` : ''}{m.errore ? ` · ${m.errore}` : ''}</p>
                  </div>
                </div>
              ))}
              <div ref={fondo} />
            </div>
            {data.finestra_aperta ? (
              <div className="flex gap-2 items-end">
                <CampoRisposta value={testo} onChange={setTesto} placeholder="Scrivi una risposta"
                               onInvia={() => { if (testo.trim() && !inviando) invia(); }} />
                <Button size="sm" disabled={!testo || inviando} onClick={invia} className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full shrink-0">
                  {inviando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Invia'}
                </Button>
              </div>
            ) : (
              <p className="text-[11px] text-[#5b5346]">
                Finestra di 24 ore chiusa: per riaprire la conversazione serve un template approvato.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ============ VENDITE: analytics aggregati (Admin only) ============ */
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };

function SalesSection() {
  const [start, setStart] = useState(daysAgo(29));
  const [end, setEnd] = useState(isoDay(new Date()));
  const [metric, setMetric] = useState('revenue');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [showSilent, setShowSilent] = useState(false);

  useEffect(() => {
    setLoading(true); setError(null);
    axios.get(`${API}/admin/sales-analytics`, { params: { start, end }, headers: getH() })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Impossibile caricare i dati di vendita.'))
      .finally(() => setLoading(false));
  }, [start, end]);

  const setPreset = (days) => { setStart(daysAgo(days - 1)); setEnd(isoDay(new Date())); };
  const setYear = () => { const y = new Date().getFullYear(); setStart(`${y}-01-01`); setEnd(isoDay(new Date())); };
  const setAll = () => { setStart(data?.range?.data_from || '2015-01-01'); setEnd(isoDay(new Date())); };

  const exportCsv = () => {
    const rows = [['Promotore', 'Email', 'Professione', 'Vendite', 'Fatturato', 'Provvigioni', 'Scontrino medio', 'Prima vendita', 'Ultima vendita', 'Vendite totali storiche', 'Ultimo accesso portale']];
    const num = (v) => String(Number(v || 0).toFixed(2)).replace('.', ',');
    (data?.leaderboard || []).forEach(r => rows.push([
      r.name, r.email || '', r.professional_type || '', r.referrals, num(r.revenue), num(r.amount),
      num(r.referrals ? r.revenue / r.referrals : 0),
      r.first_sale || '', r.last_sale || '', r.lifetime_referrals, r.last_login || '',
    ]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url; a.download = `vendite-promotori_${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (openId) return <UserDossier userId={openId} onClose={() => setOpenId(null)} backLabel="Torna alle vendite" />;

  const t = data?.totals;
  const prev = data?.previous;
  const delta = (cur, before) => {
    if (!before) return cur ? null : 0;
    return Math.round(((cur - before) / before) * 100);
  };
  const series = data?.series || [];
  const metricOf = (s) => (metric === 'revenue' ? s.revenue : metric === 'amount' ? s.amount : s.referrals);
  const maxVal = Math.max(1, ...series.map(metricOf));
  const lb = data?.leaderboard || [];
  const maxLb = lb[0]?.revenue || 1;

  return (
    <div className="space-y-4" data-testid="admin-sales">
      {/* Selettore periodo */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            {[{ l: '7 giorni', f: () => setPreset(7) }, { l: '30 giorni', f: () => setPreset(30) },
              { l: '90 giorni', f: () => setPreset(90) }, { l: 'Anno in corso', f: setYear }, { l: 'Sempre', f: setAll }].map((b, i) => (
              <button key={i} onClick={b.f} className="px-3 py-1 rounded-full text-xs font-medium border bg-white text-[#4a4238] border-gray-200 hover:border-[#5f7d52] hover:text-[#12100c] transition-all">{b.l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <CalendarDays className="w-4 h-4 text-[#5b5346]" />
            <Input type="date" value={start} max={end} onChange={e => e.target.value && setStart(e.target.value)} className="h-8 w-[9.5rem] rounded-xl text-xs" data-testid="sales-start" />
            <span className="text-xs text-[#5b5346]">→</span>
            <Input type="date" value={end} min={start} onChange={e => e.target.value && setEnd(e.target.value)} className="h-8 w-[9.5rem] rounded-xl text-xs" data-testid="sales-end" />
          </div>
        </CardContent>
      </Card>

      {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" /> : error ? (
        <Card className="rounded-2xl border-gray-100 shadow-sm"><CardContent className="p-5 flex items-center gap-2 text-sm text-[#B45454]"><AlertCircle className="w-4 h-4" /> {error}</CardContent></Card>
      ) : !data ? null : (
        <>
          <p className="text-[11px] text-[#5b5346]">
            Periodo: {fmtDay(data.range.start)} → {fmtDay(data.range.end)} ({data.range.days} giorni) ·
            confronto con {fmtDay(data.range.prev_start)} → {fmtDay(data.range.prev_end)}
            {data.range.data_from && ` · dati disponibili dal ${fmtDay(data.range.data_from)}`}
          </p>

          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiDelta icon={Wallet} value={fmtEur(t.revenue)} label="Fatturato generato" color="#12100c" delta={delta(t.revenue, prev.revenue)} prev={fmtEur(prev.revenue)} />
            <KpiDelta icon={ShoppingBag} value={t.referrals} label="Vendite" color="#5f7d52" delta={delta(t.referrals, prev.referrals)} prev={prev.referrals} />
            <KpiDelta icon={Euro} value={fmtEur(t.commissions)} label="Provvigioni" color="#c6a15b" delta={delta(t.commissions, prev.commissions)} prev={fmtEur(prev.commissions)} />
            <KpiDelta icon={TrendingUp} value={fmtEur(t.avg_order)} label="Scontrino medio" color="#4a4238" delta={delta(t.avg_order, prev.avg_order)} prev={fmtEur(prev.avg_order)} />
            <KpiDelta icon={Users} value={`${t.promoters} / ${t.promoters_total}`} label="Promotori che hanno venduto" color="#4a4238" delta={delta(t.promoters, prev.promoters)} prev={prev.promoters} />
          </div>
          {t.referrals > 0 && (
            <p className="text-[11px] text-[#5b5346]">
              Le provvigioni pesano il {t.commission_ratio}% del fatturato generato. Il fatturato è il totale
              degli ordini WooCommerce collegati, spedizione e imposte incluse; ordini annullati, falliti o
              rimborsati non vengono contati.
              {t.revenue_estimated > 0 && ` ${t.revenue_estimated} vendite sono stimate sul valore dei prodotti (ordine non più leggibile).`}
              {t.revenue_missing > 0 && ` ${t.revenue_missing} vendite non hanno un ordine recuperabile e valgono zero.`}
            </p>
          )}

          {/* Andamento */}
          <Card className="rounded-2xl border-gray-100 shadow-sm">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm text-[#12100c] flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Andamento {data.range.group === 'day' ? 'giornaliero' : 'mensile'}
              </CardTitle>
              <div className="flex gap-1">
                {[{ id: 'revenue', l: 'Fatturato' }, { id: 'amount', l: 'Provvigioni' }, { id: 'referrals', l: 'Vendite' }].map(m => (
                  <button key={m.id} onClick={() => setMetric(m.id)}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all ${metric === m.id ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200'}`}>{m.l}</button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {series.every(s => !s.referrals) ? <p className="text-xs text-[#4a4238]">Nessuna vendita nel periodo selezionato.</p> : (
                <div className="flex items-end gap-[3px] h-40">
                  {series.map((s, i) => {
                    const v = metricOf(s);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1 group"
                        title={`${data.range.group === 'day' ? s.key : fmtMonth(s.key)}: ${s.referrals} vendite · ${fmtEur(s.revenue)} di fatturato · ${fmtEur(s.amount)} di provvigioni`}>
                        <div className="w-full bg-[#5f7d52] rounded-t transition-all group-hover:bg-[#12100c]" style={{ height: `${(v / maxVal) * 100}%`, minHeight: v ? '3px' : '1px' }} />
                        {series.length <= 32 && <span className="text-[8px] text-[#5b5346] truncate w-full text-center">{data.range.group === 'day' ? fmtDate(s.key) : fmtMonth(s.key)}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
                  {data.best_bucket && data.best_bucket.referrals > 0 && (
                <p className="text-[10px] text-[#5b5346] mt-2">
                  Picco: {data.range.group === 'day' ? fmtDay(data.best_bucket.key) : fmtMonth(data.best_bucket.key)} con {fmtEur(data.best_bucket.revenue)} di fatturato su {data.best_bucket.referrals} vendite.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Classifica promotori */}
          <Card className="rounded-2xl border-gray-100 shadow-sm">
            <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Trophy className="w-4 h-4" /> Promotori che hanno venduto ({lb.length})</CardTitle>
              {lb.length > 0 && <Button size="sm" variant="outline" onClick={exportCsv} className="rounded-full h-7 px-3 text-[10px]"><Download className="w-3 h-3 mr-1" /> CSV</Button>}
            </CardHeader>
            <CardContent className="px-0">
              {lb.length === 0 ? <p className="text-xs text-[#4a4238] px-6">Nessuna vendita nel periodo.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[#5b5346] text-[10px] uppercase tracking-wide border-b border-gray-100">
                        <th className="text-left font-semibold px-4 py-2">Promotore</th>
                        <th className="text-left font-semibold px-2 py-2 hidden md:table-cell">Quota</th>
                        <th className="text-right font-semibold px-2 py-2">Vendite</th>
                        <th className="text-right font-semibold px-2 py-2">Fatturato</th>
                        <th className="text-right font-semibold px-2 py-2 hidden sm:table-cell">Provvigioni</th>
                        <th className="text-left font-semibold px-2 py-2 hidden md:table-cell">Ultima vendita</th>
                        <th className="text-left font-semibold px-4 py-2 hidden lg:table-cell">Ultimo accesso</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lb.map((r, i) => (
                        <tr key={i} className={`border-b border-gray-50 ${r.profile_id ? 'hover:bg-gray-50/50 cursor-pointer' : ''}`}
                          onClick={() => r.profile_id && setOpenId(r.profile_id)}>
                          <td className="px-4 py-2">
                            <div className="font-medium text-[#12100c] truncate max-w-[200px] flex items-center gap-1.5">
                              {r.name}
                              {!r.in_portal && <Badge variant="outline" className="text-[8px] text-[#5b5346]">fuori portale</Badge>}
                            </div>
                            <div className="text-[10px] text-[#5b5346] truncate max-w-[200px]">{r.email || `Affiliato #${r.affiliate_id}`}</div>
                          </td>
                          <td className="px-2 py-2 hidden md:table-cell w-32">
                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-[#5f7d52] rounded-full" style={{ width: `${Math.min((r.revenue / maxLb) * 100, 100)}%` }} /></div>
                          </td>
                          <td className="px-2 py-2 text-right text-[#4a4238]">{r.referrals}</td>
                          <td className="px-2 py-2 text-right font-semibold text-[#12100c]">{fmtEur(r.revenue)}</td>
                          <td className="px-2 py-2 text-right text-[#4a4238] hidden sm:table-cell">{fmtEur(r.amount)}</td>
                          <td className="px-2 py-2 hidden md:table-cell text-[#4a4238]">{fmtDay(r.last_sale) || 'n/d'}</td>
                          <td className="px-4 py-2 hidden lg:table-cell">
                            {r.last_login ? <span className="text-[#4a4238]">{fmtDate(r.last_login)}</span>
                              : <Badge variant="outline" className="text-[10px] text-[#B45454] border-[#E8C9C9]">Mai entrato</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Promotori fermi */}
          <Card className="rounded-2xl border-gray-100 shadow-sm">
            <CardHeader className="pb-2">
              <button onClick={() => setShowSilent(v => !v)} className="w-full flex items-center justify-between">
                <CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><AlertCircle className="w-4 h-4" /> Nessuna vendita nel periodo ({t.promoters_silent})</CardTitle>
                <ChevronDown className={`w-4 h-4 text-[#5b5346] transition-transform ${showSilent ? 'rotate-180' : ''}`} />
              </button>
            </CardHeader>
            {showSilent && (
              <CardContent className="px-0">
                <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
                  {(data.silent_promoters || []).map((r, i) => (
                    <button key={i} onClick={() => r.profile_id && setOpenId(r.profile_id)}
                      className="w-full flex items-center gap-3 px-6 py-2 text-left hover:bg-gray-50/50">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#12100c] truncate">{r.name}</p>
                        <p className="text-[10px] text-[#5b5346] truncate">{r.email}</p>
                      </div>
                      {r.lifetime_referrals > 0
                        ? <Badge variant="outline" className="text-[10px] text-[#4a4238]">{r.lifetime_referrals} vendite in passato · ultima {fmtDay(r.last_sale)}</Badge>
                        : <Badge variant="outline" className="text-[10px] text-[#B45454] border-[#E8C9C9]">Mai venduto</Badge>}
                      {!r.last_login && <Badge variant="outline" className="text-[10px] text-[#5b5346]">Mai entrato</Badge>}
                    </button>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>

          <div className="grid lg:grid-cols-2 gap-4">
            {/* Stato provvigioni */}
            <Card className="rounded-2xl border-gray-100 shadow-sm">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><Wallet className="w-4 h-4" /> Stato delle provvigioni</CardTitle></CardHeader>
              <CardContent>
                {!data.by_status?.length ? <p className="text-xs text-[#4a4238]">Nessuna provvigione nel periodo.</p> : (
                  <div className="space-y-2">
                    {data.by_status.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs">
                        <Badge className={`text-[9px] border-none ${refStatus(s.status).cls}`}>{refStatus(s.status).label}</Badge>
                        <span className="flex-1 text-[#4a4238]">{s.count} vendite</span>
                        <span className="font-semibold text-[#12100c]">{fmtEur(s.amount)}</span>
                      </div>
                    ))}
                    {t.new_promoters > 0 && (
                      <p className="text-[10px] text-[#5b5346] pt-2 border-t border-gray-100">
                        Nuovi promotori iscritti nel periodo: {t.new_promoters}
                        {data.new_promoters?.length > 0 && ` (${data.new_promoters.slice(0, 5).map(n => n.name).join(', ')}${data.new_promoters.length > 5 ? '…' : ''})`}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ultime vendite */}
            <Card className="rounded-2xl border-gray-100 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Ultime vendite del periodo</CardTitle>
                <p className="text-[10px] text-[#5b5346]">Importo dell'ordine e, a destra, la provvigione.</p>
              </CardHeader>
              <CardContent className="px-0">
                {!data.recent_referrals?.length ? <p className="text-xs text-[#4a4238] px-6">Nessuna vendita nel periodo.</p> : (
                  <div className="divide-y divide-gray-50 max-h-96 overflow-y-auto">
                    {data.recent_referrals.map((s, i) => (
                      <div key={i} className="flex items-center gap-3 px-6 py-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#12100c] truncate">{s.promoter}</p>
                          <p className="text-[10px] text-[#5b5346] truncate" title={s.description}>{fmtDay(s.date)}{s.description ? ` · ${s.description}` : ''}</p>
                        </div>
                        <Badge className={`text-[8px] border-none ${refStatus(s.status).cls}`}>{refStatus(s.status).label}</Badge>
                        <span className="text-xs font-semibold text-[#12100c] w-20 text-right" title={s.order_estimated ? 'Stimato sul valore dei prodotti' : `Ordine ${s.reference}`}>
                          {fmtEur(s.order_total)}{s.order_estimated ? '*' : ''}
                        </span>
                        <span className="text-[10px] text-[#5b5346] w-14 text-right">{fmtEur(s.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function KpiDelta({ icon: Icon, value, label, color, delta, prev }) {
  const up = delta != null && delta > 0;
  const down = delta != null && delta < 0;
  return (
    <Card className="rounded-xl border-gray-100 shadow-sm">
      <CardContent className="p-3">
        <div className="flex items-center gap-2.5">
          <Icon className="w-4 h-4 shrink-0" style={{ color }} strokeWidth={1.5} />
          <div className="min-w-0">
            <p className="text-lg font-bold truncate" style={{ color }}>{value}</p>
            <p className="text-[10px] text-[#4a4238]">{label}</p>
          </div>
        </div>
        {delta != null && (
          <div className="flex items-center gap-1 mt-1.5 text-[10px]">
            {up && <ArrowUp className="w-3 h-3 text-[#4a5f43]" />}
            {down && <ArrowDown className="w-3 h-3 text-[#B45454]" />}
            <span className={up ? 'text-[#4a5f43]' : down ? 'text-[#B45454]' : 'text-[#5b5346]'}>{up ? '+' : ''}{delta}%</span>
            <span className="text-[#5b5346]">vs {prev} periodo prec.</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============ TRASH SECTION (Admin only) ============ */
function TrashSection({ canPurge }) {
  const tables = ['studies', 'protocols', 'materials', 'feedbacks', 'announcements'];
  const labels = { studies: 'Studi', protocols: 'Protocolli', materials: 'Materiali', feedbacks: 'Feedback', announcements: 'Comunicazioni' };
  const [activeTable, setActiveTable] = useState('studies');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async (t) => {
    setLoading(true);
    try { const r = await axios.get(`${API}/admin/trash/${t}`, { headers: getH() }); setItems(r.data || []); } catch { setItems([]); }
    setLoading(false);
  }, []);

  useEffect(() => { fetch(activeTable); }, [activeTable, fetch]);

  const restore = async (id) => {
    try { await axios.post(`${API}/admin/trash/${activeTable}/${id}/restore`, {}, { headers: getH() }); toast.success('Ripristinato'); fetch(activeTable); } catch { toast.error('Errore'); }
  };
  const permDelete = async (id) => {
    try { await axios.delete(`${API}/admin/trash/${activeTable}/${id}`, { headers: getH() }); toast.success('Eliminato permanentemente'); fetch(activeTable); } catch { toast.error('Errore'); }
  };

  return (
    <div data-testid="admin-trash">
      <p className="text-[11px] text-[#5b5346] mb-3">
        {canPurge
          ? "Ripristina o elimina definitivamente. L'eliminazione definitiva non è recuperabile."
          : "Da qui puoi ripristinare quello che è stato cestinato. L'eliminazione definitiva è riservata al superamministratore."}
      </p>
      <div className="flex gap-2 mb-4">{tables.map(t => (
        <button key={t} onClick={() => setActiveTable(t)} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${activeTable === t ? 'bg-[#12100c] text-white border-[#12100c]' : 'bg-white text-[#4a4238] border-gray-200'}`}>{labels[t]}</button>
      ))}</div>
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-0">
          {loading ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto" /></div> :
           items.length === 0 ? <p className="p-6 text-sm text-[#4a4238] text-center">Cestino vuoto per {labels[activeTable]}</p> : (
            <div className="divide-y divide-gray-100">{items.map(item => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-3" data-testid={`trash-${item.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#12100c] truncate">{item.title || item.professional_name || 'n/d'}</p>
                  <p className="text-[10px] text-[#4a4238]">Eliminato il {item.deleted_at ? new Date(item.deleted_at).toLocaleDateString('it-IT') : 'n/d'}</p>
                </div>
                <Button size="sm" onClick={() => restore(item.id)} className="bg-[#5f7d52] text-white rounded-full h-7 px-2 text-[10px]" data-testid={`restore-${item.id}`}><RotateCcw className="w-3 h-3 mr-1" />Ripristina</Button>
                {canPurge && <Button size="sm" variant="outline" onClick={() => permDelete(item.id)} className="rounded-full h-7 px-2 text-[10px] border-red-200 text-red-600" data-testid={`perm-delete-${item.id}`}><Trash2 className="w-3 h-3 mr-1" />Elimina</Button>}
              </div>
            ))}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ============ SETTINGS ============ */
function SettingsSection() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { axios.get(`${API}/admin/stats`, { headers: getH() }).then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);
  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;
  if (!stats) return null;
  return (
    <Card className="rounded-2xl border-gray-100 shadow-sm" data-testid="admin-settings">
      <CardHeader><CardTitle className="text-sm text-[#12100c] flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Statistiche</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatMini icon={Users} value={stats.total_users} label="Utenti" color="#12100c" />
          <StatMini icon={UserCheck} value={stats.approved_users} label="Approvati" color="#5f7d52" />
        </div>
        {Object.keys(stats.user_types || {}).length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-semibold text-[#12100c] mb-2">Utenti per tipo</p>
            {Object.entries(stats.user_types).sort((a,b)=>b[1]-a[1]).map(([type,count]) => (
              <div key={type} className="flex items-center gap-2 mb-1.5">
                <span className="text-xs text-[#4a4238] w-32 truncate">{PROF_LABELS[type]||type}</span>
                <div className="flex-1 h-2 bg-gray-100 rounded-full"><div className="h-full bg-[#12100c] rounded-full" style={{width:`${(count/stats.total_users)*100}%`}} /></div>
                <span className="text-xs font-bold text-[#12100c] w-6 text-right">{count}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============ GENERIC CRUD ============ */
function CrudSection({ table, label, endpoint, fields, isAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async () => {
    try { const r = await axios.get(`${API}/${endpoint}`, { headers: getH() }); setItems(Array.isArray(r.data) ? r.data : []); } catch {} finally { setLoading(false); }
  }, [endpoint]);
  useEffect(() => { fetch(); }, [fetch]);

  const startCreate = () => { setForm(fields.reduce((a,f)=>({...a,[f.key]:f.default??''}),{})); setEditing('new'); };
  const startEdit = (item) => { setForm({...item}); setEditing(item); };
  const cancel = () => { setEditing(null); setForm({}); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === 'new') { await axios.post(`${API}/${endpoint}`, form, { headers: getH() }); toast.success('Creato'); }
      else { await axios.patch(`${API}/${endpoint}/${form.id}`, form, { headers: getH() }); toast.success('Aggiornato'); }
      fetch(); cancel();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setSaving(false);
  };

  const softDelete = async (id) => {
    try {
      // Editor: soft delete. Admin: also soft delete (permanent from trash)
      await axios.delete(`${API}/editor/${table}/${id}`, { headers: getH() });
      toast.success('Spostato nel cestino');
      fetch();
    } catch { toast.error('Errore'); }
    setDeleting(null);
  };

  if (loading) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;

  if (editing !== null) {
    return (
      <div data-testid={`${table}-form`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold text-[#12100c]">{editing === 'new' ? `Nuovo` : `Modifica`} {label}</h2>
          <Button variant="ghost" size="sm" onClick={cancel}><X className="w-4 h-4 mr-1" /> Annulla</Button>
        </div>
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardContent className="p-5 space-y-4">
            {fields.filter(f => !f.hidden).map(f => (
              <div key={f.key}>
                <Label className="text-[#12100c] text-sm font-medium">{f.label}</Label>
                {f.type === 'textarea' ? <textarea value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} rows={f.rows||3} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-[#c6a15b] focus:ring-1 focus:ring-[#c6a15b] focus:outline-none" />
                : f.type === 'list' ? <textarea value={Array.isArray(form[f.key]) ? form[f.key].join('\n') : form[f.key] || ''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value.split('\n').filter(s=>s.trim())}))} rows={f.rows||5} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:border-[#c6a15b] focus:ring-1 focus:ring-[#c6a15b] focus:outline-none font-mono" placeholder="Un elemento per riga" />
                : f.type === 'json' ? <textarea value={typeof form[f.key] === 'object' && form[f.key] !== null ? JSON.stringify(form[f.key], null, 2) : form[f.key] || ''} onChange={e=>{try{setForm(p=>({...p,[f.key]:JSON.parse(e.target.value)}))}catch{setForm(p=>({...p,[f.key+'_raw']:e.target.value}))}}} rows={f.rows||6} className="mt-1 w-full rounded-xl border border-gray-300 px-3 py-2 text-xs focus:border-[#c6a15b] focus:ring-1 focus:ring-[#c6a15b] focus:outline-none font-mono" />
                : f.type === 'select' ? <Select value={form[f.key]||''} onValueChange={v=>setForm(p=>({...p,[f.key]:v}))}><SelectTrigger className="mt-1 rounded-xl border-gray-300"><SelectValue placeholder="Seleziona..." /></SelectTrigger><SelectContent>{f.options.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
                : f.type === 'toggle' ? <div className="mt-1"><Switch checked={!!form[f.key]} onCheckedChange={v=>setForm(p=>({...p,[f.key]:v}))} /></div>
                : f.type === 'number' ? <Input type="number" value={form[f.key]??''} onChange={e=>setForm(p=>({...p,[f.key]:parseInt(e.target.value)||0}))} className="mt-1 rounded-xl border-gray-300" />
                : f.type === 'rating' ? <div className="flex gap-1 mt-1">{[1,2,3,4,5].map(i=><button key={i} type="button" onClick={()=>setForm(p=>({...p,[f.key]:i}))}><Star className={`w-5 h-5 ${i<=(form[f.key]||0)?'fill-[#c6a15b] text-[#8a6620]':'text-gray-300'}`}/></button>)}</div>
                : f.type === 'tags' ? <Input value={Array.isArray(form[f.key])?form[f.key].join(', '):form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value.split(',').map(s=>s.trim()).filter(Boolean)}))} placeholder="tag1, tag2" className="mt-1 rounded-xl border-gray-300" />
                : <Input value={form[f.key]||''} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} className="mt-1 rounded-xl border-gray-300" />}
                {f.hint && <p className="text-[10px] text-[#4a4238] mt-0.5">{f.hint}</p>}
              </div>
            ))}
            <div className="flex gap-2 pt-2 sticky bottom-0 bg-white pb-2">
              <Button onClick={save} disabled={saving} className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full">{saving?<Loader2 className="w-4 h-4 animate-spin mr-1"/>:<Save className="w-4 h-4 mr-1"/>} Salva</Button>
              <Button variant="outline" onClick={cancel} className="rounded-full">Annulla</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid={`${table}-list`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold text-[#12100c]">{label} ({items.length})</h2>
        <Button size="sm" onClick={startCreate} className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full text-xs" data-testid={`${table}-add-btn`}><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi</Button>
      </div>
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-0">
          {items.length === 0 ? <p className="p-6 text-sm text-[#4a4238] text-center">Nessun elemento</p> : (
            <div className="divide-y divide-gray-100">{items.map(item => (
              <div key={item.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/50" data-testid={`${table}-row-${item.id}`}>
                {item.icon && <span className="text-lg shrink-0">{item.icon}</span>}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#12100c] truncate">{item.title||item.professional_name||'n/d'}</p>
                  <p className="text-[10px] text-[#4a4238] truncate">{item.authors?`${item.authors} (${item.year})`:''}{item.difficulty?` ${item.difficulty}`:''}{item.category?` ${item.category}`:''}{item.type?` ${item.type}`:''}</p>
                </div>
                {item.is_published !== undefined && <Badge className={`text-[8px] ${item.is_published?'bg-[#5f7d52]/10 text-[#4a5f43]':'bg-gray-100 text-gray-500'}`}>{item.is_published?'Pub':'Bozza'}</Badge>}
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 px-2.5 text-[10px] rounded-full border-gray-300 text-[#12100c]" onClick={()=>startEdit(item)} data-testid={`edit-${item.id}`}>
                    <Pencil className="w-3 h-3 mr-1"/>Modifica
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 px-2.5 text-[10px] rounded-full border-red-200 text-red-500" onClick={()=>setDeleting(item.id)} data-testid={`delete-${item.id}`}>
                    <Trash2 className="w-3 h-3 mr-1"/>Elimina
                  </Button>
                </div>
              </div>
            ))}</div>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!deleting} onOpenChange={()=>setDeleting(null)}>
        <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle className="text-[#12100c]">Sposta nel cestino?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4a4238]">{isAdmin ? "L'elemento sarà spostato nel cestino. Potrai ripristinarlo dalla sezione Cestino." : "L'elemento sarà spostato nel cestino. L'admin potrà ripristinarlo."}</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={()=>setDeleting(null)} className="rounded-full">Annulla</Button>
            <Button onClick={()=>softDelete(deleting)} className="bg-red-600 hover:bg-red-700 text-white rounded-full">Sposta nel cestino</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============ RICHIESTE: i lead della campagna (Admin only) ============ */
const STATI_LEAD = {
  nuovo: 'Nuovo', contattato: 'Contattato', in_conversazione: 'In conversazione',
  appuntamento: 'Appuntamento', promotore: 'Diventato promotore',
  non_interessato: 'Non interessato', silenzio: 'Nessuna risposta', non_valido: 'Numero non valido',
};
const statoLeadCls = (s) => s === 'appuntamento' ? 'bg-[#5f7d52]/15 text-[#3f5636]'
  : s === 'promotore' ? 'bg-[#c6a15b]/20 text-[#8a6620]'
  : s === 'in_conversazione' ? 'bg-blue-100 text-blue-700'
  : s === 'nuovo' ? 'bg-[#12100c]/10 text-[#12100c]'
  : 'bg-gray-100 text-[#4a4238]';

function RichiesteSection({ apri = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stato, setStato] = useState('tutti');
  const [q, setQ] = useState('');
  const [aperto, setAperto] = useState(apri);

  const carica = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (stato !== 'tutti') p.set('stato', stato);
    if (q.trim()) p.set('q', q.trim());
    axios.get(`${API}/admin/lead?${p.toString()}`, { headers: getH() })
      .then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [stato, q]);
  useEffect(() => { const t = setTimeout(carica, 250); return () => clearTimeout(t); }, [carica]);

  if (loading && !data) return <Loader2 className="w-5 h-5 animate-spin text-[#12100c] mx-auto my-8" />;
  if (!data) return <p className="text-sm text-[#4a4238]">Impossibile caricare le richieste.</p>;

  const r = data.riepilogo || {};
  const oggi = r.ultime_24_ore || {};
  const mese = r.ultimi_30_giorni || {};
  const perStato = Object.fromEntries((r.per_stato || []).map(s => [s.stato, s.n]));
  const cal = data.calendario || {};
  const avvisi = [];
  if (!r.attivo) avvisi.push('manca LEAD_WEBHOOK_SECRET nel .env: Pabbly non può consegnare i lead');
  if (!data.bot?.attivo) avvisi.push('il bot è spento: le risposte restano in coda a una persona');
  if (data.bot?.superato) avvisi.push('tetto di spesa settimanale raggiunto: il bot non risponde più finché non rientra');
  if (!cal.ok || !cal.amelia) avvisi.push('il calendario non risponde: gli appuntamenti si fissano a mano');

  return (
    <div className="space-y-4" data-testid="admin-lead">
      {avvisi.length > 0 && (
        <Card className="rounded-2xl border-amber-100 shadow-sm bg-amber-50/60">
          <CardContent className="p-4">
            <p className="text-sm font-medium text-[#12100c] flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#8a6620]" /> La macchina gira solo in parte
            </p>
            <ul className="text-xs text-[#4a4238] mt-1.5 space-y-0.5">
              {avvisi.map(a => <li key={a}>· {a}</li>)}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatMini icon={Inbox} value={oggi.arrivati || 0} label="Arrivate nelle ultime 24 ore" color="#12100c" />
        <StatMini icon={MessageCircle} value={perStato.in_conversazione || 0} label="In conversazione col bot" color="#5f7d52" />
        <StatMini icon={CalendarDays} value={perStato.appuntamento || 0} label="Con chiamata fissata" color="#c6a15b" />
        <StatMini icon={AlertCircle} value={r.da_rispondere || 0} label="Da riprendere a mano" color="#B45454" />
      </div>

      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-4 text-xs text-[#4a4238] space-y-1">
          <p>
            Negli ultimi trenta giorni: {mese.totale || 0} richieste, {mese.con_appuntamento || 0} arrivate
            alla chiamata, {mese.promotori || 0} diventate promotori.
          </p>
          {data.bot && (
            <p className={data.bot.superato ? 'text-red-700' : ''}>
              Il bot ({data.bot.modello}) negli ultimi sette giorni è costato {(data.bot.speso ?? 0).toFixed(2)} $
              su un tetto di {data.bot.tetto} $, in {data.bot.turni || 0} risposte.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2 items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email o numero"
               className="rounded-xl text-xs max-w-xs" />
        <Select value={stato} onValueChange={setStato}>
          <SelectTrigger className="rounded-xl text-xs w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli stati</SelectItem>
            {Object.entries(STATI_LEAD).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={carica} className="rounded-full text-xs">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Aggiorna
        </Button>
      </div>

      <div className="space-y-2 md:hidden">
        {!data.lead?.length && (
          <p className="text-sm text-[#4a4238] text-center py-6">Nessuna richiesta.</p>
        )}
        {data.lead?.map(l => (
          <Card key={l.id} onClick={() => setAperto(l.id)}
                className="rounded-2xl border-gray-100 shadow-sm active:bg-gray-50 cursor-pointer">
            <CardContent className="p-3.5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium text-[#12100c]">
                    {[l.nome, l.cognome].filter(Boolean).join(' ') || 'Senza nome'}
                  </p>
                  <p className="text-[11px] text-[#5b5346]">{l.professione || l.telefono || l.email}</p>
                </div>
                <Badge className={`text-[10px] border-none shrink-0 ${statoLeadCls(l.stato)}`}>
                  {STATI_LEAD[l.stato] || l.stato}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {l.escalation && <Badge className="text-[10px] border-none bg-red-100 text-red-700">da riprendere</Badge>}
                {!l.bot_attivo && <Badge className="text-[10px] border-none bg-gray-100 text-[#4a4238]">bot fermo</Badge>}
                {l.appuntamento_at && <Badge className="text-[10px] border-none bg-[#5f7d52]/15 text-[#3f5636]">
                  chiamata {fmtDateTime(l.appuntamento_at)}
                </Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl border-gray-100 shadow-sm hidden md:block">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[#5b5346]">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Persona</th>
                <th className="text-left font-medium px-4 py-2.5">Professione</th>
                <th className="text-left font-medium px-4 py-2.5">Stato</th>
                <th className="text-left font-medium px-4 py-2.5">Chiamata</th>
                <th className="text-left font-medium px-4 py-2.5">Arrivata</th>
              </tr>
            </thead>
            <tbody>
              {!data.lead?.length && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-[#4a4238]">Nessuna richiesta.</td></tr>
              )}
              {data.lead?.map(l => (
                <tr key={l.id} onClick={() => setAperto(l.id)}
                    className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-[#12100c]">
                      {[l.nome, l.cognome].filter(Boolean).join(' ') || 'Senza nome'}
                    </span>
                    <span className="block text-[10px] text-[#5b5346]">{l.telefono || l.email}</span>
                  </td>
                  <td className="px-4 py-2.5 text-[#4a4238]">{l.professione || '—'}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`text-[10px] border-none ${statoLeadCls(l.stato)}`}>
                      {STATI_LEAD[l.stato] || l.stato}
                    </Badge>
                    {l.escalation && <Badge className="ml-1 text-[10px] border-none bg-red-100 text-red-700">da riprendere</Badge>}
                    {!l.bot_attivo && <Badge className="ml-1 text-[10px] border-none bg-gray-100 text-[#4a4238]">bot fermo</Badge>}
                  </td>
                  <td className="px-4 py-2.5 text-[#4a4238]">{l.appuntamento_at ? fmtDateTime(l.appuntamento_at) : '—'}</td>
                  <td className="px-4 py-2.5 text-[#5b5346]">{fmtDateTime(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {aperto && <RichiestaDettaglio id={aperto} onClose={() => setAperto(null)} onChange={carica} />}

      <RisposteDelBot />
    </div>
  );
}

/* Quello che il bot ha detto a tutti, di fila. Nel primo periodo è la cosa che
   si guarda più spesso: si legge come parla ai contatti veri e si correggono le
   istruzioni sulle frasi vere invece che su quelle immaginate. */
function RisposteDelBot() {
  const [data, setData] = useState(null);
  const [aperta, setAperta] = useState(false);

  useEffect(() => {
    if (!aperta || data) return;
    axios.get(`${API}/admin/bot/turni?limite=40`, { headers: getH() })
      .then(r => setData(r.data)).catch(() => setData({ turni: [] }));
  }, [aperta, data]);

  return (
    <Card className="rounded-2xl border-gray-100 shadow-sm">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm text-[#12100c] flex items-center gap-2">
          <Bot className="w-4 h-4" /> Come risponde il bot
        </CardTitle>
        <Button variant="ghost" size="sm" className="text-xs"
                onClick={() => { setAperta(!aperta); if (aperta) setData(null); }}>
          {aperta ? 'Chiudi' : 'Leggi le ultime risposte'}
        </Button>
      </CardHeader>
      {aperta && (
        <CardContent>
          {!data ? <Loader2 className="w-4 h-4 animate-spin text-[#12100c]" /> : (
            <>
              {!data.turni?.length && <p className="text-xs text-[#4a4238]">Nessuna risposta ancora.</p>}
              <div className="space-y-3">
                {data.turni?.map((t, i) => (
                  <div key={i} className="border-l-2 border-gray-100 pl-3">
                    <p className="text-[10px] text-[#5b5346]">
                      {[t.nome, t.cognome].filter(Boolean).join(' ') || 'senza nome'} · {fmtDateTime(t.at)}
                      {t.strumenti?.length ? ` · ${t.strumenti.join(', ')}` : ''} · {t.costo} $
                      {t.errore ? ` · errore: ${t.errore}` : ''}
                    </p>
                    {t.domanda && <p className="text-xs text-[#4a4238] mt-1">
                      <span className="text-[#5b5346]">Gli hanno scritto:</span> {t.domanda}
                    </p>}
                    <p className="text-xs text-[#12100c] whitespace-pre-wrap mt-0.5">{t.risposta || '—'}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function RichiestaDettaglio({ id, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [testo, setTesto] = useState('');
  const [inviando, setInviando] = useState(false);
  const riquadro = useRef(null);
  const fondo = useRef(null);

  const carica = useCallback(() => {
    axios.get(`${API}/admin/lead/${id}`, { headers: getH() })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [id]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { riquadro.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [id]);
  useEffect(() => { if (data) fondo.current?.scrollIntoView({ block: 'end' }); }, [data]);

  const modifica = async (campi, messaggio) => {
    try {
      await axios.patch(`${API}/admin/lead/${id}`, campi, { headers: getH() });
      toast.success(messaggio); carica(); onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Non riuscito');
    }
  };

  const invia = async () => {
    setInviando(true);
    try {
      await axios.post(`${API}/admin/whatsapp/send`, { telefono: data.lead.telefono, testo },
                       { headers: getH() });
      setTesto(''); carica(); onChange?.();
      toast.success('Messaggio inviato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invio non riuscito');
    }
    setInviando(false);
  };

  const l = data?.lead;
  const app = (data?.appuntamenti || []).find(a => a.stato === 'confermato');

  return (
    <Card ref={riquadro} className="rounded-2xl border-[#c6a15b]/40 shadow-sm scroll-mt-4">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm text-[#12100c] flex items-center gap-2">
          <Inbox className="w-4 h-4" />
          {l ? ([l.nome, l.cognome].filter(Boolean).join(' ') || l.telefono || 'Richiesta') : 'Richiesta'}
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
      </CardHeader>
      <CardContent>
        {!data ? <Loader2 className="w-4 h-4 animate-spin text-[#12100c]" /> : (
          <>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-xs text-[#4a4238] mb-3">
              <p>Numero: {l.telefono || '—'}</p>
              <p>Email: {l.email || '—'}</p>
              <p>Professione: {l.professione || '—'}</p>
              <p>Città: {l.citta || '—'}</p>
              <p>Campagna: {l.campagna || '—'}</p>
              <p>Stato: {STATI_LEAD[l.stato] || l.stato}</p>
              {app && <p className="md:col-span-2 text-[#3f5636]">
                Chiamata fissata per il {fmtDateTime(app.inizio)}, {app.durata_minuti} minuti,
                fissata {app.creato_da === 'bot' ? 'dal bot' : 'a mano'}.
              </p>}
              {l.escalation && <p className="md:col-span-2 text-red-700">
                Il bot ha alzato la mano: {l.escalation_motivo || 'motivo non registrato'}.
              </p>}
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              <Button variant="outline" size="sm" className="rounded-full text-xs"
                      onClick={() => modifica({ bot_attivo: !l.bot_attivo },
                        l.bot_attivo ? 'Da adesso rispondi tu' : 'Il bot riprende')}>
                {l.bot_attivo ? <><ShieldOff className="w-3.5 h-3.5 mr-1" /> Prendo io</>
                              : <><Bot className="w-3.5 h-3.5 mr-1" /> Riattiva il bot</>}
              </Button>
              {l.escalation && (
                <Button variant="outline" size="sm" className="rounded-full text-xs"
                        onClick={() => modifica({ escalation: false }, 'Segnata come risolta')}>
                  <CheckCircle className="w-3.5 h-3.5 mr-1" /> Risolta
                </Button>
              )}
              {l.stato !== 'promotore' && (
                <Button variant="outline" size="sm" className="rounded-full text-xs"
                        onClick={() => modifica({ stato: 'promotore' }, 'Segnata come diventata promotore')}>
                  <UserCheck className="w-3.5 h-3.5 mr-1" /> È diventato promotore
                </Button>
              )}
              {l.stato !== 'non_interessato' && (
                <Button variant="outline" size="sm" className="rounded-full text-xs"
                        onClick={() => modifica({ stato: 'non_interessato' }, 'Chiusa')}>
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Non interessato
                </Button>
              )}
            </div>

            <div className="space-y-2 max-h-72 overflow-y-auto mb-3">
              {!data.messaggi?.length && <p className="text-xs text-[#4a4238]">Nessun messaggio.</p>}
              {data.messaggi?.map((m, i) => (
                <div key={i} className={`flex ${m.direzione === 'in' ? 'justify-start' : 'justify-end'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${m.direzione === 'in' ? 'bg-gray-100 text-[#12100c]' : 'bg-[#5f7d52]/10 text-[#12100c]'}`}>
                    <p className="text-xs whitespace-pre-wrap">{m.testo}</p>
                    <p className="text-[9px] text-[#5b5346] mt-1">
                      {fmtDateTime(m.at)}{m.autore === 'bot' ? ' · bot' : ''}{m.stato ? ` · ${m.stato}` : ''}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={fondo} />
            </div>

            {data.finestra_aperta ? (
              <div className="flex gap-2 items-end">
                <CampoRisposta value={testo} onChange={setTesto}
                               placeholder={l.bot_attivo ? 'Scrivi tu (il bot resta acceso)' : 'Scrivi una risposta'}
                               onInvia={() => { if (testo.trim() && !inviando) invia(); }} />
                <Button size="sm" disabled={!testo || inviando} onClick={invia}
                        className="bg-[#12100c] hover:bg-[#5f7d52] text-white rounded-full shrink-0">
                  {inviando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Invia'}
                </Button>
              </div>
            ) : (
              <p className="text-[11px] text-[#5b5346]">
                Finestra di 24 ore chiusa: per riaprire la conversazione serve un template approvato.
              </p>
            )}

            {data.turni_bot?.length > 0 && (
              <details className="mt-4">
                <summary className="text-xs text-[#5b5346] cursor-pointer">
                  Cosa ha fatto il bot ({data.turni_bot.length} turni)
                </summary>
                <div className="mt-2 space-y-2">
                  {data.turni_bot.map((t, i) => (
                    <div key={i} className="text-[11px] text-[#4a4238] border-l-2 border-gray-100 pl-2">
                      <p className="text-[#5b5346]">{fmtDateTime(t.at)}
                        {t.strumenti?.length ? ` · ${t.strumenti.join(', ')}` : ''}
                        {t.errore ? ` · errore: ${t.errore}` : ''}</p>
                      <p className="whitespace-pre-wrap">{t.risposta || '—'}</p>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function StatMini({ icon: Icon, value, label, color }) {
  return <Card className="rounded-xl border-gray-100 shadow-sm"><CardContent className="p-3 flex items-center gap-2.5"><Icon className="w-4 h-4 shrink-0" style={{color}} strokeWidth={1.5}/><div><p className="text-lg font-bold" style={{color}}>{value}</p><p className="text-[10px] text-[#4a4238]">{label}</p></div></CardContent></Card>;
}

/* ============ FIELD DEFINITIONS ============ */
const studyFields = [
  { key:'title',label:'Titolo',type:'text',isTitle:true },{ key:'authors',label:'Autori',type:'text' },{ key:'year',label:'Anno',type:'number' },{ key:'journal',label:'Rivista',type:'text' },
  { key:'doi',label:'DOI',type:'text' },{ key:'pubmed_url',label:'URL PubMed',type:'text' },
  { key:'area_tags',label:'Aree tematiche',type:'tags',hint:'Microbiota, Immunità, Antiossidanti, Detossificazione, Metabolismo, Cognitività, Sicurezza e tossicologia' },
  { key:'study_type',label:'Tipo studio',type:'select',options:[{value:'in_vitro',label:'In vitro'},{value:'animal',label:'Animale'},{value:'human_clinical',label:'Clinico umano'},{value:'review',label:'Review'},{value:'meta_analysis',label:'Meta-analisi'}] },
  { key:'relevance_score',label:'Rilevanza (1-3)',type:'number' },{ key:'key_finding',label:'Risultato chiave',type:'textarea',rows:2 },{ key:'summary_it',label:'Sintesi in italiano',type:'textarea',rows:5 },
  { key:'methodology',label:'Metodologia',type:'text' },{ key:'display_order',label:'Ordine',type:'number',default:0 },{ key:'is_published',label:'Pubblicato',type:'toggle',default:true },
];
const protocolFields = [
  { key:'icon',label:'Icona',type:'text' },{ key:'title',label:'Titolo',type:'text',isTitle:true },{ key:'slug',label:'Slug',type:'text',hint:'URL-friendly' },
  { key:'target_description',label:'Per chi',type:'textarea',rows:2 },{ key:'objective',label:'Obiettivo',type:'textarea',rows:2 },
  { key:'duration_weeks',label:'Durata sett. (0=cont.)',type:'number',default:0 },
  { key:'difficulty',label:'Difficolta',type:'select',options:[{value:'base',label:'Base'},{value:'intermedio',label:'Intermedio'},{value:'avanzato',label:'Avanzato'}] },
  { key:'phases',label:'Fasi del protocollo (JSON)',type:'json',rows:10,hint:'Array di fasi: [{"phase_name":"Nome","week_range":"Sett. 1-2","dosage":"5ml x 2","timing":"Mattina + sera","instructions":"...","notes":""}]' },
  { key:'expected_results',label:'Risultati attesi (JSON)',type:'json',rows:5,hint:'Oggetto: {"2_weeks":"Risultato...","4_weeks":"Risultato...","8_weeks":"Risultato..."}' },
  { key:'synergies',label:'Sinergie',type:'textarea',rows:2 },{ key:'contraindications',label:'Controindicazioni',type:'textarea',rows:2 },
  { key:'area_tags',label:'Aree correlate',type:'tags' },{ key:'display_order',label:'Ordine',type:'number',default:0 },{ key:'is_published',label:'Pubblicato',type:'toggle',default:true },
];
const materialFields = [
  { key:'title',label:'Titolo',type:'text',isTitle:true },{ key:'description',label:'Descrizione',type:'textarea',rows:2 },
  { key:'category',label:'Categoria',type:'select',options:[{value:'scheda_paziente',label:'Scheda paziente'},{value:'brochure',label:'Brochure'},{value:'presentazione',label:'Presentazione'},{value:'social_kit',label:'Social kit'},{value:'email_template',label:'Template email'},{value:'scientific_dossier',label:'Dossier scientifico'},{value:'altro',label:'Altro'}] },
  { key:'file_url',label:'URL File',type:'text' },{ key:'file_type',label:'Tipo file',type:'text' },
  { key:'professional_types',label:'Profili',type:'tags' },{ key:'coming_soon',label:'In arrivo',type:'toggle',default:true },
  { key:'display_order',label:'Ordine',type:'number',default:0 },{ key:'is_published',label:'Pubblicato',type:'toggle',default:true },
];
const feedbackFields = [
  { key:'professional_name',label:'Nome professionista',type:'text',isTitle:true },
  { key:'professional_type',label:'Tipo',type:'select',options:Object.entries(PROF_LABELS).map(([v,l])=>({value:v,label:l})) },
  { key:'role_title',label:'Qualifica estesa',type:'textarea',rows:2,hint:'Titoli, specializzazioni, associazioni. Compare sotto il nome.' },
  { key:'subtitle',label:'Titolo della relazione',type:'text' },
  { key:'summary',label:'Sintesi (card chiusa)',type:'textarea',rows:3,hint:"2-3 righe: cosa ha osservato e su che scala. È quello che il promotore legge prima di aprire." },
  { key:'subject',label:'Soggetto',type:'select',options:[{value:'umano',label:'Persone'},{value:'animali_affezione',label:'Animali d’affezione'},{value:'allevamento',label:'Allevamento'}],default:'umano' },
  { key:'product_line',label:'Linea di prodotto',type:'select',options:[{value:'umiful',label:'UMIFUL'},{value:'umivet',label:'UMIVET (zootecnica)'}],default:'umiful' },
  { key:'sample_size',label:'Numerosità',type:'text',hint:'es. ~60 clienti, 1.047 suini' },
  { key:'observation_period',label:'Periodo di osservazione',type:'text',hint:'es. 90 giorni, ~18 mesi' },
  { key:'location',label:'Luogo',type:'text' },
  { key:'key_numbers',label:'Numeri chiave (JSON)',type:'json',rows:6,hint:'Array: [{"value":"1.047","label":"capi nel gruppo"}]. Massimo 4, altrimenti la griglia va a capo.' },
  { key:'body',label:'Corpo della relazione (JSON a blocchi)',type:'json',rows:16,hint:'Blocchi: {"type":"p","text":"..."} | {"type":"h","text":"..."} | {"type":"ul","items":[...]} | {"type":"quote","text":"..."} | {"type":"note","text":"..."} | {"type":"gallery","title":"...","layout":"before_after|grid","sensitive":true,"note":"...","images":[{"url":"/api/media/feedback/x.jpg","caption":"Prima"}]} | {"type":"video","title":"..."}' },
  { key:'feedback_text',label:'Citazione breve (fallback)',type:'textarea',rows:3,hint:"Usata solo se il corpo a blocchi è vuoto." },
  { key:'video_id',label:'ID video Vadoo',type:'text',hint:"Solo l'id, quello dopo iframe_test?id= . Serve un blocco di tipo video nel corpo." },
  { key:'video_poster',label:'URL poster video',type:'text',hint:'Opzionale: se vuoto usa la thumbnail generata da Vadoo.' },
  { key:'avatar_url',label:'URL avatar o logo',type:'text' },
  { key:'document_url',label:'URL documento originale',type:'text' },
  { key:'editorial_note',label:'Nota redazionale',type:'textarea',rows:2,hint:"In coda alla relazione, in corsivo. Per chiarimenti nostri, non dell'autore." },
  { key:'is_anonymous',label:'Anonimo',type:'toggle',default:false },
  { key:'area_tags',label:'Aree',type:'tags' },
  { key:'display_order',label:'Ordine',type:'number',default:0 },
  { key:'is_featured',label:'In evidenza',type:'toggle',default:false },
  { key:'is_published',label:'Pubblicato',type:'toggle',default:true },
];
const announcementFields = [
  { key:'title',label:'Titolo',type:'text',isTitle:true },{ key:'content',label:'Contenuto',type:'textarea',rows:4 },
  { key:'icon',label:'Icona',type:'text',default:'\u{1f4e2}' },
  { key:'type',label:'Tipo',type:'select',options:[{value:'info',label:'Info'},{value:'update',label:'Aggiornamento'},{value:'important',label:'Importante'},{value:'event',label:'Evento'}] },
  { key:'link_url',label:'URL link',type:'text' },{ key:'link_text',label:'Testo link',type:'text' },
  { key:'is_pinned',label:'Fissato',type:'toggle',default:false },{ key:'is_published',label:'Pubblicato',type:'toggle',default:true },
];
const areaFields = [
  { key:'icon',label:'Icona (emoji)',type:'text' },
  { key:'title',label:'Titolo',type:'text',isTitle:true },
  { key:'slug',label:'Slug (URL)',type:'text',hint:'es. salute-intestinale' },
  { key:'short_description',label:'Breve descrizione (max 50 car.)',type:'text' },
  { key:'priority',label:'Priorità',type:'select',options:[{value:'alta',label:'Alta'},{value:'media',label:'Media'},{value:'bassa',label:'Bassa'}] },
  { key:'when_to_use',label:'Quando consigliarlo',type:'list',rows:8,hint:'Un punto per riga (es. "Disbiosi intestinale diagnosticata")' },
  { key:'mechanism',label:'Perché funziona (meccanismo)',type:'textarea',rows:5 },
  { key:'protocol',label:'Protocollo consigliato',type:'list',rows:5,hint:'Un punto per riga (es. "Dosaggio: 5 ml x 2/die")' },
  { key:'expected_results',label:'Risultati attesi',type:'list',rows:4,hint:'Un punto per riga (es. "1-2 settimane: riduzione gonfiore")' },
  { key:'synergies',label:'Sinergie',type:'textarea',rows:2 },
  { key:'ready_phrase',label:'Frase pronta per il paziente',type:'textarea',rows:3 },
  { key:'precautions',label:'Note precauzionali',type:'textarea',rows:2 },
  { key:'display_order',label:'Ordine visualizzazione',type:'number',default:0 },
  { key:'is_published',label:'Pubblicato',type:'toggle',default:true },
];
